import React, { useEffect, useMemo, useState } from "react";
import { apiClient } from "@/lib/api";
import {
  Play,
  PlayCircle,
  RefreshCcw,
  AlertTriangle,
  Trash2,
  TrendingUp,
  TrendingDown,
  Target,
  Sparkles,
  Eye,
  AlertOctagon,
  CheckCircle2,
  XCircle,
  Users,
  Award,
  ListChecks,
  ClipboardList,
  Check,
  X,
  Zap,
  ArrowRight,
  MessageSquare,
  Send,
  Lightbulb,
  Copy,
  ArrowUpDown,
} from "lucide-react";

const PERSONA_ORDER = ["beginner", "professional", "sales", "student", "builder"];
const PERSONA_SHORT = {
  beginner: "Beginner",
  professional: "Professional",
  sales: "Sales",
  student: "Student",
  builder: "Builder",
};

const PRIORITY_COLOR = {
  P0: { bg: "rgba(255,92,42,0.10)", border: "rgba(255,92,42,0.35)", color: "var(--accent)" },
  P1: { bg: "rgba(79,70,229,0.08)", border: "rgba(79,70,229,0.30)", color: "var(--indigo)" },
  P2: { bg: "rgba(15,15,18,0.06)", border: "rgba(15,15,18,0.16)", color: "var(--ink-2)" },
};

const SEVERITY_COLOR = {
  high: { bg: "rgba(255,92,42,0.10)", color: "var(--accent)" },
  medium: { bg: "rgba(234,179,8,0.10)", color: "#a16207" },
  low: { bg: "rgba(15,15,18,0.06)", color: "var(--ink-2)" },
};

const STAGES_ORDER = ["first_visit", "first_session", "day_1", "week_1"];
const STAGE_LABEL = {
  first_visit: "First visit",
  first_session: "First session",
  day_1: "Day 1",
  week_1: "Week 1",
};

// Tiny localStorage helpers so filter selections + Q&A history survive
// re-renders (e.g. after the admin patches a recommendation). All keys are
// namespaced under `effectiveness:` to avoid collisions.
function loadLS(key, fallback) {
  try {
    const v = window.localStorage.getItem(`effectiveness:${key}`);
    return v === null ? fallback : v;
  } catch (_e) {
    return fallback;
  }
}
function saveLS(key, value) {
  try {
    window.localStorage.setItem(`effectiveness:${key}`, value);
  } catch (_e) { /* quota / privacy mode */ }
}

export default function EffectivenessPanel() {
  const [personas, setPersonas] = useState([]);
  const [depths, setDepths] = useState([]);
  const [persona, setPersona] = useState("professional");
  const [depth, setDepth] = useState("first_session");
  const [note, setNote] = useState("");
  const [running, setRunning] = useState(false);
  const [batchRunning, setBatchRunning] = useState(false);
  const [reports, setReports] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [openDoc, setOpenDoc] = useState(null);
  const [batches, setBatches] = useState([]);
  const [openBatchId, setOpenBatchId] = useState(null);
  const [openBatchDoc, setOpenBatchDoc] = useState(null);
  const [loadingList, setLoadingList] = useState(true);
  const [error, setError] = useState(null);

  async function loadPersonas() {
    try {
      const r = await apiClient.get("/admin/effectiveness/personas");
      setPersonas(r.data.personas || []);
      setDepths(r.data.depths || []);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    }
  }

  async function loadReports() {
    setLoadingList(true);
    try {
      const [reps, btchs] = await Promise.all([
        apiClient.get("/admin/effectiveness/reports"),
        apiClient.get("/admin/effectiveness/batches"),
      ]);
      setReports(reps.data.reports || []);
      setBatches(btchs.data.batches || []);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setLoadingList(false);
    }
  }

  async function openBatch(id) {
    setOpenBatchId(id);
    setOpenBatchDoc(null);
    setOpenId(null);
    setOpenDoc(null);
    try {
      const r = await apiClient.get(`/admin/effectiveness/batches/${id}`);
      setOpenBatchDoc(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    }
  }

  async function runAllPersonas() {
    setBatchRunning(true);
    setError(null);
    try {
      const r = await apiClient.post("/admin/effectiveness/run-all", {
        depth,
        note: note.trim() || undefined,
      });
      setNote("");
      const id = r.data?.batch_id;
      await loadReports();
      if (id) {
        const deadline = Date.now() + 10 * 60_000; // batches can take up to 5-6min
        const poll = async () => {
          try {
            const d = await apiClient.get(`/admin/effectiveness/batches/${id}`);
            const status = d.data?.status;
            setOpenBatchId(id);
            setOpenBatchDoc(d.data);
            if (status === "running" && Date.now() < deadline) {
              setTimeout(poll, 6000);
            } else {
              await loadReports();
              setBatchRunning(false);
              if (status === "error") setError(d.data?.error || "Batch failed");
            }
          } catch (e) {
            setError(e?.response?.data?.detail || e.message);
            setBatchRunning(false);
          }
        };
        setTimeout(poll, 2000);
        return;
      }
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Batch run failed");
    }
    setBatchRunning(false);
  }

  async function removeBatch(id) {
    if (!window.confirm("Delete this multi-persona batch and its 5 individual reports?")) return;
    try {
      await apiClient.delete(`/admin/effectiveness/batches/${id}`);
      if (openBatchId === id) {
        setOpenBatchId(null);
        setOpenBatchDoc(null);
      }
      await loadReports();
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    }
  }

  async function openReport(id) {
    setOpenId(id);
    setOpenDoc(null);
    setOpenBatchId(null);
    setOpenBatchDoc(null);
    try {
      const r = await apiClient.get(`/admin/effectiveness/reports/${id}`);
      setOpenDoc(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    }
  }

  async function runSimulation() {
    setRunning(true);
    setError(null);
    try {
      const r = await apiClient.post("/admin/effectiveness/run", {
        persona,
        depth,
        note: note.trim() || undefined,
      });
      setNote("");
      const id = r.data?.report_id;
      await loadReports();
      if (id) {
        // Poll until complete/error. Reports usually finish in 30-120s.
        const deadline = Date.now() + 5 * 60_000;
        const poll = async () => {
          try {
            const d = await apiClient.get(`/admin/effectiveness/reports/${id}`);
            const status = d.data?.status;
            setOpenId(id);
            setOpenDoc(d.data);
            if (status === "running" && Date.now() < deadline) {
              setTimeout(poll, 4000);
            } else {
              await loadReports();
              setRunning(false);
              if (status === "error") {
                setError(d.data?.error || "Simulation failed");
              }
            }
          } catch (e) {
            setError(e?.response?.data?.detail || e.message);
            setRunning(false);
          }
        };
        // First poll immediately, then every 4s.
        setTimeout(poll, 1500);
        return;
      }
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Run failed");
    }
    setRunning(false);
  }

  async function removeReport(id) {
    if (!window.confirm("Delete this report? This cannot be undone.")) return;
    try {
      await apiClient.delete(`/admin/effectiveness/reports/${id}`);
      if (openId === id) {
        setOpenId(null);
        setOpenDoc(null);
      }
      await loadReports();
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    }
  }

  useEffect(() => {
    loadPersonas();
    loadReports();
  }, []);

  return (
    <div data-testid="effectiveness-panel" className="space-y-7">
      <Intro />

      <EffectivenessAgentQA />

      <VisitorAttentionReport />

      <div className="brut p-6 md:p-7" data-testid="effectiveness-run-card">
        <div className="flex items-center gap-2.5 mb-4">
          <Target size={18} className="text-[var(--primary,#2563EB)]" />
          <h2 className="font-display text-xl tracking-tight">Run a new simulation</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Field label="Persona">
            <select
              data-testid="effectiveness-persona-select"
              value={persona}
              onChange={(e) => setPersona(e.target.value)}
              className="input-base w-full"
            >
              {personas.map((p) => (
                <option key={p.id} value={p.id}>{p.label}</option>
              ))}
            </select>
          </Field>
          <Field label="Simulation depth">
            <select
              data-testid="effectiveness-depth-select"
              value={depth}
              onChange={(e) => setDepth(e.target.value)}
              className="input-base w-full"
            >
              {depths.map((d) => (
                <option key={d} value={d}>{STAGE_LABEL[d] || d}</option>
              ))}
            </select>
          </Field>
          <Field label="Note (optional)">
            <input
              data-testid="effectiveness-note-input"
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. After moving promo into card"
              className="input-base w-full"
              maxLength={120}
            />
          </Field>
        </div>
        <div className="mt-5 flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={runSimulation}
            disabled={running || batchRunning || personas.length === 0}
            className="btn-primary"
            data-testid="effectiveness-run-btn"
          >
            {running ? <><span className="spinner" /> Running simulation…</> : <><Play size={14} /> Run simulation</>}
          </button>
          <button
            type="button"
            onClick={runAllPersonas}
            disabled={running || batchRunning || personas.length === 0}
            className="btn-duo"
            data-testid="effectiveness-run-all-btn"
            title="Fan out all 5 personas in parallel and synthesize into a board-ready conversion report"
          >
            {batchRunning ? <><span className="spinner" /> Running all 5 personas…</> : <><Users size={14} /> Run All Personas</>}
          </button>
          <button
            type="button"
            onClick={loadReports}
            disabled={loadingList}
            className="btn-ghost"
            data-testid="effectiveness-refresh-btn"
          >
            <RefreshCcw size={14} /> Refresh history
          </button>
          {(running || batchRunning) && (
            <span className="text-xs text-[var(--ink-3)]">
              Claude Sonnet 4.5 is walking through the page{batchRunning ? " as 5 personas in parallel — typically 1-3 minutes" : " as this persona — typically 25-60s"}.
            </span>
          )}
        </div>
        {error && (
          <div className="mt-4 text-sm rounded-lg px-3 py-2 flex items-start gap-2"
            style={{ background: "rgba(255,92,42,0.08)", color: "var(--accent)", border: "1px solid rgba(255,92,42,0.30)" }}
            data-testid="effectiveness-error"
          >
            <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}
      </div>

      <RecommendationTracker />

      <BatchesList
        batches={batches}
        loading={loadingList}
        openId={openBatchId}
        onOpen={openBatch}
        onDelete={removeBatch}
      />

      {openBatchId && (
        <BatchDetail doc={openBatchDoc} onClose={() => { setOpenBatchId(null); setOpenBatchDoc(null); }} />
      )}

      <ReportsList
        reports={reports}
        loading={loadingList}
        openId={openId}
        onOpen={openReport}
        onDelete={removeReport}
      />

      {openId && (
        <ReportDetail doc={openDoc} onClose={() => { setOpenId(null); setOpenDoc(null); }} />
      )}
    </div>
  );
}

function Intro() {
  return (
    <div
      className="brut p-6"
      data-testid="effectiveness-intro"
      style={{ background: "linear-gradient(135deg,#FFFFFF 0%,#F5F7FF 100%)" }}
    >
      <div className="flex items-center gap-2.5 mb-2">
        <Sparkles size={16} className="text-[var(--primary,#2563EB)]" />
        <span className="kicker">Phase 1 · Visitor-to-First-Lesson</span>
      </div>
      <h1 className="font-display text-2xl md:text-[26px] tracking-tight">
        Program Effectiveness Lab
      </h1>
      <p className="text-sm text-[var(--ink-2)] mt-2 leading-relaxed max-w-3xl">
        Diagnoses why 111 visits resulted in 0 lessons taken. Run a single persona for a focused probe, or hit <strong>Run All Personas</strong> to fan out all 5 in parallel and get a board-ready conversion report with a recommendation heatmap. Uses the same source code your visitors see — no sandbox accounts, no production data touched.
      </p>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-wider text-[var(--ink-3)] font-semibold mb-1.5">{label}</span>
      {children}
    </label>
  );
}

function ReportsList({ reports, loading, openId, onOpen, onDelete }) {
  if (loading) {
    return (
      <div className="brut p-6 text-center text-sm text-[var(--ink-3)]" data-testid="effectiveness-list-loading">
        <span className="spinner mr-2" /> Loading reports…
      </div>
    );
  }
  if (!reports.length) {
    return (
      <div className="brut p-6 text-center text-sm text-[var(--ink-3)]" data-testid="effectiveness-list-empty">
        No simulations yet. Run your first one above — start with <strong>Busy Professional · First session</strong> for the fastest read on the 111-visits problem.
      </div>
    );
  }
  return (
    <div className="space-y-2" data-testid="effectiveness-list">
      <h2 className="font-display text-lg tracking-tight mb-2">Report history</h2>
      {reports.map((r) => (
        <div
          key={r.report_id}
          className={`brut p-4 flex flex-wrap items-center gap-3 cursor-pointer transition-colors ${openId === r.report_id ? "bg-[var(--bg-2)]" : ""}`}
          onClick={() => onOpen(r.report_id)}
          data-testid={`effectiveness-row-${r.report_id}`}
        >
          <div className="flex-1 min-w-[200px]">
            <div className="font-semibold text-sm">{r.persona_label} · {STAGE_LABEL[r.depth] || r.depth}</div>
            <div className="text-xs text-[var(--ink-3)] mt-0.5">
              {new Date(r.started_at).toLocaleString()} · {r.run_by || "—"}
              {r.note ? <> · <em>{r.note}</em></> : null}
            </div>
          </div>
          <StatusPill status={r.status} />
          <button
            type="button"
            className="text-[var(--ink-3)] hover:text-[var(--accent,#FF5C2A)] p-1"
            onClick={(e) => { e.stopPropagation(); onDelete(r.report_id); }}
            data-testid={`effectiveness-delete-${r.report_id}`}
            aria-label="Delete report"
          >
            <Trash2 size={14} />
          </button>
        </div>
      ))}
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    running:  { bg: "rgba(79,70,229,0.10)",  color: "var(--indigo)",      label: "Running" },
    complete: { bg: "rgba(14,159,110,0.10)", color: "var(--emerald)",     label: "Complete" },
    error:    { bg: "rgba(255,92,42,0.10)",  color: "var(--accent)",      label: "Error" },
  };
  const s = map[status] || map.error;
  return (
    <span className="pill" style={{ background: s.bg, color: s.color, borderColor: s.color, fontSize: "0.7rem" }}>
      {s.label}
    </span>
  );
}

function ReportDetail({ doc, onClose }) {
  if (!doc) {
    return (
      <div className="brut p-6 text-center text-sm text-[var(--ink-3)]" data-testid="effectiveness-detail-loading">
        <span className="spinner mr-2" /> Loading report…
      </div>
    );
  }
  if (doc.status === "running") {
    return (
      <div className="brut p-6 text-sm text-[var(--ink-3)]" data-testid="effectiveness-detail-running">
        Simulation in progress. Refresh shortly.
        <button type="button" className="btn-ghost ml-3" onClick={onClose}>Close</button>
      </div>
    );
  }
  if (doc.status === "error") {
    return (
      <div className="brut p-6" data-testid="effectiveness-detail-error">
        <div className="flex items-start gap-2 text-[var(--accent,#FF5C2A)]">
          <AlertOctagon size={16} className="mt-0.5" />
          <div>
            <div className="font-semibold text-sm">Simulation failed</div>
            <div className="text-xs mt-1">{doc.error || "Unknown error"}</div>
          </div>
        </div>
        <button type="button" className="btn-ghost mt-4" onClick={onClose}>Close</button>
      </div>
    );
  }

  const r = doc.result || {};

  return (
    <div className="space-y-5" data-testid="effectiveness-detail">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <span className="kicker">Report · {doc.report_id}</span>
          <h2 className="font-display text-2xl tracking-tight mt-1">
            {doc.persona_label} · {STAGE_LABEL[doc.depth] || doc.depth}
          </h2>
          <div className="text-xs text-[var(--ink-3)] mt-1">
            {new Date(doc.started_at).toLocaleString()} · run by {doc.run_by || "—"}
          </div>
        </div>
        <button type="button" className="btn-ghost" onClick={onClose} data-testid="effectiveness-detail-close">
          Close
        </button>
      </div>

      <ScoreCards r={r} />

      {r.tldr && (
        <div className="brut p-5" style={{ background: "rgba(79,70,229,0.04)" }} data-testid="effectiveness-tldr">
          <div className="kicker mb-1">TL;DR</div>
          <p className="text-sm leading-relaxed">{r.tldr}</p>
        </div>
      )}

      {r.headline_diagnosis && (
        <Section title="Why 111 visits → 0 lessons (for this persona)" icon={<TrendingDown size={16} />}>
          <p className="text-sm leading-relaxed">{r.headline_diagnosis}</p>
        </Section>
      )}

      <FirstChallengeAssessment fc={r.first_challenge_assessment} />

      <DropOffPoints points={r.drop_off_points} />

      <Recommendations recs={r.recommendations} />

      <StageScores stages={r.stages} hookStrength={r.hook_strength} />

      <CriticalIssues issues={r.critical_issues} />

      <TwoCol
        left={<BulletBlock title="What works" tone="good" items={r.what_works} />}
        right={<BulletBlock title="What needs improvement" tone="bad" items={r.what_needs_improvement} />}
      />

      <TwoCol
        left={<BulletBlock title="Emotional hooks detected" tone="good" items={r.emotional_hooks_detected} />}
        right={<BulletBlock title="Missing emotional hooks" tone="bad" items={r.missing_emotional_hooks} />}
      />
    </div>
  );
}

function ScoreCards({ r }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <ScoreCard
        label="Overall program score"
        value={r.overall_score}
        testid="effectiveness-overall-score"
        accent="var(--primary,#2563EB)"
      />
      <ScoreCard
        label="Visitor → first-lesson conversion"
        value={r.visitor_conversion_score}
        testid="effectiveness-conversion-score"
        accent="var(--accent,#FF5C2A)"
      />
    </div>
  );
}

function ScoreCard({ label, value, accent, testid }) {
  const n = typeof value === "number" ? value : null;
  const pct = n != null ? Math.max(0, Math.min(100, n)) : 0;
  return (
    <div className="brut p-5" data-testid={testid}>
      <div className="text-xs uppercase tracking-wider text-[var(--ink-3)] font-semibold">{label}</div>
      <div className="font-display text-5xl mt-2" style={{ color: accent }}>
        {n != null ? n : "—"}
        <span className="text-xl text-[var(--ink-3)]">/100</span>
      </div>
      <div className="mt-3 h-2 rounded-full bg-[var(--line)]">
        <div className="h-2 rounded-full" style={{ width: `${pct}%`, background: accent }} />
      </div>
    </div>
  );
}

function FirstChallengeAssessment({ fc }) {
  if (!fc) return null;
  const flags = [
    ["feels_easy", "Feels easy"],
    ["feels_fast", "Feels fast"],
    ["feels_valuable", "Feels valuable"],
    ["feels_low_risk", "Feels low-risk"],
    ["feels_personally_relevant", "Personally relevant"],
    ["would_this_persona_start", "Would actually start"],
  ];
  return (
    <Section
      title="First challenge — would this persona start?"
      icon={<Target size={16} />}
      testid="effectiveness-first-challenge"
    >
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
        {flags.map(([k, label]) => {
          const ok = fc[k] === true;
          return (
            <div
              key={k}
              className="flex items-center gap-2 text-sm rounded-lg px-3 py-2"
              style={{
                background: ok ? "rgba(14,159,110,0.08)" : "rgba(255,92,42,0.08)",
                color: ok ? "var(--emerald)" : "var(--accent)",
                border: `1px solid ${ok ? "rgba(14,159,110,0.30)" : "rgba(255,92,42,0.30)"}`,
              }}
            >
              {ok ? <CheckCircle2 size={14} /> : <XCircle size={14} />}
              <span className="font-medium">{label}</span>
            </div>
          );
        })}
      </div>
      {fc.why_or_why_not && (
        <p className="text-sm mt-4 text-[var(--ink-2)] leading-relaxed italic">&ldquo;{fc.why_or_why_not}&rdquo;</p>
      )}
    </Section>
  );
}

function DropOffPoints({ points }) {
  if (!Array.isArray(points) || !points.length) return null;
  return (
    <Section title="Drop-off points" icon={<TrendingDown size={16} />} testid="effectiveness-dropoffs">
      <div className="space-y-2.5">
        {points.map((p, i) => {
          const sev = SEVERITY_COLOR[p.severity] || SEVERITY_COLOR.medium;
          return (
            <div key={i} className="rounded-xl border border-[var(--line)] p-3">
              <div className="flex items-center gap-2 mb-1">
                <span className="pill" style={{ background: sev.bg, color: sev.color, borderColor: sev.color, fontSize: "0.65rem" }}>
                  {(p.severity || "medium").toUpperCase()}
                </span>
                <span className="text-xs text-[var(--ink-3)] uppercase tracking-wider">{STAGE_LABEL[p.stage] || p.stage}</span>
              </div>
              <div className="text-sm font-semibold">{p.moment}</div>
              <div className="text-sm text-[var(--ink-2)] mt-1">{p.why}</div>
            </div>
          );
        })}
      </div>
    </Section>
  );
}

function Recommendations({ recs }) {
  if (!Array.isArray(recs) || !recs.length) return null;
  return (
    <Section title="Recommended changes" icon={<Sparkles size={16} />} testid="effectiveness-recs">
      <div className="space-y-2.5">
        {recs.map((r, i) => {
          const c = PRIORITY_COLOR[r.priority] || PRIORITY_COLOR.P2;
          return (
            <div key={i} className="rounded-xl border border-[var(--line)] p-3">
              <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                <span className="pill" style={{ background: c.bg, color: c.color, borderColor: c.color, fontSize: "0.65rem" }}>
                  {r.priority || "P2"}
                </span>
                <span className="text-xs text-[var(--ink-3)] uppercase tracking-wider">{r.area}</span>
                {r.expected_lift && (
                  <span className="text-xs text-[var(--emerald)] inline-flex items-center gap-1">
                    <TrendingUp size={12} /> {r.expected_lift}
                  </span>
                )}
              </div>
              <div className="text-sm leading-relaxed">{r.change}</div>
            </div>
          );
        })}
      </div>
    </Section>
  );
}

function StageScores({ stages, hookStrength }) {
  if (!stages || typeof stages !== "object") return null;
  const active = STAGES_ORDER.filter((s) => stages[s]);
  if (!active.length) return null;
  return (
    <Section title="Stage-by-stage ratings" icon={<TrendingUp size={16} />} testid="effectiveness-stage-scores">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider text-[var(--ink-3)]">
              <th className="py-2 pr-3">Stage</th>
              <th className="py-2 px-2">Hook</th>
              <th className="py-2 px-2">Clarity</th>
              <th className="py-2 px-2">Engagement</th>
              <th className="py-2 px-2">Value</th>
              <th className="py-2 px-2">Retention</th>
              <th className="py-2 px-2">Paid likely</th>
            </tr>
          </thead>
          <tbody>
            {active.map((s) => {
              const v = stages[s] || {};
              const hook = hookStrength?.[s];
              return (
                <tr key={s} className="border-t border-[var(--line)]">
                  <td className="py-2 pr-3 font-semibold">{STAGE_LABEL[s]}</td>
                  <ScoreCell v={hook} />
                  <ScoreCell v={v.clarity} />
                  <ScoreCell v={v.engagement} />
                  <ScoreCell v={v.perceived_value} />
                  <ScoreCell v={v.retention_likelihood} />
                  <ScoreCell v={v.paid_conversion_likelihood} />
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="mt-3 space-y-2">
        {active.map((s) => stages[s]?.notes ? (
          <div key={s} className="text-xs">
            <span className="uppercase tracking-wider text-[var(--ink-3)] font-semibold mr-2">{STAGE_LABEL[s]}:</span>
            <span className="text-[var(--ink-2)] italic">&ldquo;{stages[s].notes}&rdquo;</span>
          </div>
        ) : null)}
      </div>
    </Section>
  );
}

function ScoreCell({ v }) {
  if (v == null) return <td className="py-2 px-2 text-[var(--ink-3)]">—</td>;
  const tone = v >= 7 ? "var(--emerald)" : v >= 4 ? "#a16207" : "var(--accent)";
  return <td className="py-2 px-2 font-semibold" style={{ color: tone }}>{v}/10</td>;
}

function CriticalIssues({ issues }) {
  if (!Array.isArray(issues) || !issues.length) return null;
  return (
    <Section title="Critical issues" icon={<AlertOctagon size={16} />} testid="effectiveness-critical">
      <div className="space-y-2">
        {issues.map((it, i) => (
          <div key={i} className="rounded-xl border p-3" style={{ borderColor: "rgba(255,92,42,0.30)", background: "rgba(255,92,42,0.04)" }}>
            <div className="flex items-center justify-between gap-2 mb-0.5">
              <div className="font-semibold text-sm">{it.title}</div>
              {it.impact && (
                <span className="text-[10px] uppercase tracking-wider font-semibold text-[var(--accent)]">
                  {String(it.impact).replace(/_/g, " ")}
                </span>
              )}
            </div>
            <div className="text-sm text-[var(--ink-2)]">{it.detail}</div>
          </div>
        ))}
      </div>
    </Section>
  );
}

function BulletBlock({ title, tone, items }) {
  if (!Array.isArray(items) || !items.length) return null;
  const color = tone === "good" ? "var(--emerald)" : "var(--accent,#FF5C2A)";
  return (
    <div className="brut p-4">
      <div className="text-xs uppercase tracking-wider font-semibold mb-2.5" style={{ color }}>{title}</div>
      <ul className="space-y-1.5 text-sm">
        {items.map((it, i) => <li key={i} className="leading-relaxed">• {it}</li>)}
      </ul>
    </div>
  );
}

function TwoCol({ left, right }) {
  if (!left && !right) return null;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {left}
      {right}
    </div>
  );
}

function Section({ title, icon, children, testid }) {
  return (
    <div className="brut p-5" data-testid={testid}>
      <div className="flex items-center gap-2 mb-3">
        {icon}
        <h3 className="font-display text-lg tracking-tight">{title}</h3>
      </div>
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Multi-Persona Batch UI
// ---------------------------------------------------------------------------

function BatchesList({ batches, loading, openId, onOpen, onDelete }) {
  if (loading && !batches.length) return null;
  return (
    <div className="space-y-2" data-testid="effectiveness-batches-list">
      <h2 className="font-display text-lg tracking-tight mb-2 flex items-center gap-2">
        <Users size={16} /> Multi-persona batches
      </h2>
      {batches.length === 0 ? (
        <div className="brut p-5 text-sm text-[var(--ink-3)]" data-testid="effectiveness-batches-empty">
          No batch runs yet. Hit <strong>Run All Personas</strong> above to generate a board-ready conversion report.
        </div>
      ) : (
        batches.map((b) => (
          <div
            key={b.batch_id}
            className={`brut p-4 flex flex-wrap items-center gap-3 cursor-pointer transition-colors ${openId === b.batch_id ? "bg-[var(--bg-2)]" : ""}`}
            onClick={() => onOpen(b.batch_id)}
            data-testid={`effectiveness-batch-row-${b.batch_id}`}
          >
            <div className="flex-1 min-w-[200px]">
              <div className="font-semibold text-sm flex items-center gap-2">
                <Users size={14} className="text-[var(--primary,#2563EB)]" />
                All 5 personas · {STAGE_LABEL[b.depth] || b.depth}
                {Array.isArray(b.completed_personas) && b.completed_personas.length > 0 && (
                  <span className="text-xs text-[var(--ink-3)] font-normal">
                    · {b.completed_personas.length}/5 complete
                  </span>
                )}
              </div>
              <div className="text-xs text-[var(--ink-3)] mt-0.5">
                {new Date(b.started_at).toLocaleString()} · {b.run_by || "—"}
                {b.note ? <> · <em>{b.note}</em></> : null}
              </div>
            </div>
            <StatusPill status={b.status} />
            <button
              type="button"
              className="text-[var(--ink-3)] hover:text-[var(--accent,#FF5C2A)] p-1"
              onClick={(e) => { e.stopPropagation(); onDelete(b.batch_id); }}
              data-testid={`effectiveness-batch-delete-${b.batch_id}`}
              aria-label="Delete batch"
            >
              <Trash2 size={14} />
            </button>
          </div>
        ))
      )}
    </div>
  );
}


function BatchDetail({ doc, onClose }) {
  if (!doc) {
    return (
      <div className="brut p-6 text-center text-sm text-[var(--ink-3)]" data-testid="effectiveness-batch-loading">
        <span className="spinner mr-2" /> Loading batch…
      </div>
    );
  }
  if (doc.status === "running") {
    const completed = doc.completed_personas?.length || 0;
    const statuses = doc.report_statuses || [];
    return (
      <div className="brut p-6 space-y-3" data-testid="effectiveness-batch-running">
        <div className="flex items-center justify-between gap-3">
          <h2 className="font-display text-xl tracking-tight flex items-center gap-2">
            <span className="spinner" /> Running batch · {completed}/5 personas complete
          </h2>
          <button type="button" className="btn-ghost" onClick={onClose}>Close</button>
        </div>
        <div className="text-xs text-[var(--ink-3)]">
          5 personas running in parallel via Claude Sonnet 4.5 + a final synthesis pass. Total time typically 1-3 minutes.
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-2">
          {PERSONA_ORDER.map((p) => {
            const r = statuses.find((s) => s.persona === p);
            const st = r?.status || "running";
            const tone = st === "complete" ? "var(--emerald)" : st === "error" ? "var(--accent)" : "var(--indigo)";
            return (
              <div key={p} className="rounded-xl border border-[var(--line)] p-2.5 text-center">
                <div className="text-xs uppercase tracking-wider font-semibold text-[var(--ink-3)]">{PERSONA_SHORT[p]}</div>
                <div className="text-xs mt-1 font-semibold" style={{ color: tone }}>
                  {st === "running" ? "Running…" : st === "complete" ? "Done" : "Error"}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }
  if (doc.status === "error") {
    return (
      <div className="brut p-6" data-testid="effectiveness-batch-error">
        <div className="flex items-start gap-2 text-[var(--accent,#FF5C2A)]">
          <AlertOctagon size={16} className="mt-0.5" />
          <div>
            <div className="font-semibold text-sm">Batch failed</div>
            <div className="text-xs mt-1">{doc.error || "Unknown error"}</div>
            {Array.isArray(doc.completed_personas) && doc.completed_personas.length > 0 && (
              <div className="text-xs mt-2 text-[var(--ink-3)]">
                {doc.completed_personas.length}/5 personas completed before synthesis broke.
              </div>
            )}
          </div>
        </div>
        <button type="button" className="btn-ghost mt-4" onClick={onClose}>Close</button>
      </div>
    );
  }

  const s = doc.synthesis || {};

  return (
    <div className="space-y-5" data-testid="effectiveness-batch-detail">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <span className="kicker">Multi-Persona Conversion Report · {doc.batch_id}</span>
          <h2 className="font-display text-2xl tracking-tight mt-1">
            All 5 personas · {STAGE_LABEL[doc.depth] || doc.depth}
          </h2>
          <div className="text-xs text-[var(--ink-3)] mt-1">
            {new Date(doc.started_at).toLocaleString()} · run by {doc.run_by || "—"}
            {doc.completed_personas?.length && doc.completed_personas.length < 5
              ? ` · ${doc.completed_personas.length}/5 personas completed (synthesis used those)`
              : ""}
          </div>
        </div>
        <button type="button" className="btn-ghost" onClick={onClose} data-testid="effectiveness-batch-close">
          Close
        </button>
      </div>

      <BatchAverages s={s} />
      <BoardReady diagnosis={s.board_ready_diagnosis} />
      <TopFiveFixes fixes={s.top_5_fixes} />
      <RecommendationHeatmap heatmap={s.heatmap} />
      <UniversalBlockers blockers={s.universal_blockers} />
      <WouldStartComparison data={s.would_start_comparison} />
      <DropOffsByPersona data={s.biggest_drop_offs_by_persona} />
      <PersonaSpecificBlockers data={s.persona_specific_blockers} />
      <SharedRecommendations recs={s.shared_recommendations} />
    </div>
  );
}


function BatchAverages({ s }) {
  const scores = s.persona_scores || [];
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <ScoreCard label="Avg overall score" value={s.avg_overall_score} accent="var(--primary,#2563EB)" testid="effectiveness-batch-avg-overall" />
      <ScoreCard label="Avg visitor → first-lesson conversion" value={s.avg_visitor_conversion_score} accent="var(--accent,#FF5C2A)" testid="effectiveness-batch-avg-conversion" />
      <div className="brut p-5" data-testid="effectiveness-batch-persona-scores">
        <div className="text-xs uppercase tracking-wider text-[var(--ink-3)] font-semibold mb-3">Per persona</div>
        <div className="space-y-1.5 text-sm">
          {scores.length === 0 ? <div className="text-[var(--ink-3)]">—</div> :
            scores.map((p) => (
              <div key={p.persona} className="flex items-center justify-between gap-2">
                <span className="text-[var(--ink-2)]">{p.label || PERSONA_SHORT[p.persona] || p.persona}</span>
                <span className="font-mono text-xs">
                  <span style={{ color: scoreColor(p.overall) }}>{p.overall ?? "—"}</span>
                  <span className="text-[var(--ink-3)]"> / </span>
                  <span style={{ color: scoreColor(p.conversion) }}>{p.conversion ?? "—"}</span>
                </span>
              </div>
            ))
          }
          <div className="text-[10px] text-[var(--ink-3)] pt-1 border-t border-[var(--line)] mt-2">overall / conversion</div>
        </div>
      </div>
    </div>
  );
}


function scoreColor(n) {
  if (typeof n !== "number") return "var(--ink-3)";
  if (n >= 70) return "var(--emerald)";
  if (n >= 50) return "#a16207";
  return "var(--accent,#FF5C2A)";
}


function BoardReady({ diagnosis }) {
  if (!diagnosis) return null;
  return (
    <Section title="Board-Ready Conversion Diagnosis" icon={<Award size={16} />} testid="effectiveness-board-ready">
      <div className="space-y-3 text-sm leading-relaxed">
        {diagnosis.why_visitors_not_starting && (
          <BoardRow label="Why visitors are not starting" body={diagnosis.why_visitors_not_starting} />
        )}
        {Array.isArray(diagnosis.biggest_universal_blockers) && diagnosis.biggest_universal_blockers.length > 0 && (
          <BoardRow
            label="Biggest universal blockers"
            body={
              <ul className="space-y-1">
                {diagnosis.biggest_universal_blockers.map((b, i) => <li key={i}>• {b}</li>)}
              </ul>
            }
          />
        )}
        {diagnosis.fix_first && (
          <BoardRow label="Fix first (ship this week)" body={diagnosis.fix_first} accent />
        )}
        {diagnosis.what_moves_the_needle && (
          <BoardRow label="What moves the needle" body={diagnosis.what_moves_the_needle} />
        )}
        {Array.isArray(diagnosis.highest_impact_lowest_complexity) && diagnosis.highest_impact_lowest_complexity.length > 0 && (
          <BoardRow
            label="High impact, low complexity"
            body={
              <ul className="space-y-1">
                {diagnosis.highest_impact_lowest_complexity.map((b, i) => <li key={i}>• {b}</li>)}
              </ul>
            }
          />
        )}
      </div>
    </Section>
  );
}

function BoardRow({ label, body, accent }) {
  return (
    <div className={`rounded-xl p-3 ${accent ? "border" : ""}`}
      style={accent ? { borderColor: "rgba(255,92,42,0.30)", background: "rgba(255,92,42,0.04)" } : { background: "var(--bg-2)" }}
    >
      <div className="text-[10px] uppercase tracking-wider text-[var(--ink-3)] font-semibold mb-1">{label}</div>
      <div className="text-sm">{body}</div>
    </div>
  );
}


function TopFiveFixes({ fixes }) {
  if (!Array.isArray(fixes) || !fixes.length) return null;
  return (
    <Section title="Top 5 fixes to lift first-lesson starts" icon={<ListChecks size={16} />} testid="effectiveness-top-fixes">
      <div className="space-y-2.5">
        {fixes.sort((a, b) => (a.rank || 99) - (b.rank || 99)).map((f, i) => {
          const c = { low: "var(--emerald)", medium: "#a16207", high: "var(--accent)" }[f.complexity] || "var(--ink-3)";
          return (
            <div key={i} className="rounded-xl border border-[var(--line)] p-3.5">
              <div className="flex items-center gap-2 flex-wrap mb-1.5">
                <span className="inline-flex items-center justify-center w-6 h-6 rounded-full font-bold text-xs"
                  style={{ background: "var(--ink, #0F1419)", color: "#fff" }}>{f.rank || i + 1}</span>
                <span className="text-xs text-[var(--ink-3)] uppercase tracking-wider">{f.area}</span>
                <span className="text-xs px-1.5 py-0.5 rounded-full font-semibold" style={{ color: c, background: `${c}1A` }}>
                  {f.complexity || "—"} complexity
                </span>
                {Array.isArray(f.personas_unblocked) && (
                  <span className="text-xs text-[var(--emerald)]">unblocks {f.personas_unblocked.length}/5</span>
                )}
              </div>
              <div className="text-sm font-semibold leading-relaxed">{f.change}</div>
              {f.why_high_impact && (
                <div className="text-xs text-[var(--ink-2)] mt-1.5 italic">Why: {f.why_high_impact}</div>
              )}
              {f.expected_lift && (
                <div className="text-xs text-[var(--emerald)] mt-1 inline-flex items-center gap-1">
                  <TrendingUp size={11} /> {f.expected_lift}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </Section>
  );
}


function RecommendationHeatmap({ heatmap }) {
  if (!Array.isArray(heatmap) || !heatmap.length) return null;
  // Sort: P0 first, then by # personas affected desc
  const rows = [...heatmap].sort((a, b) => {
    const rank = { P0: 0, P1: 1, P2: 2 };
    if ((rank[a.priority] ?? 9) !== (rank[b.priority] ?? 9)) return (rank[a.priority] ?? 9) - (rank[b.priority] ?? 9);
    return (b.personas?.length || 0) - (a.personas?.length || 0);
  });
  return (
    <Section title="Recommendation heatmap" icon={<TrendingUp size={16} />} testid="effectiveness-heatmap">
      <div className="text-xs text-[var(--ink-3)] mb-3">
        Columns are personas. A filled cell = this fix is raised for that persona. Darker = higher priority.
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider text-[var(--ink-3)]">
              <th className="py-2 pr-3 sticky left-0 bg-white">Recommendation</th>
              <th className="py-2 px-2">Priority</th>
              {PERSONA_ORDER.map((p) => (
                <th key={p} className="py-2 px-2 text-center">{PERSONA_SHORT[p]}</th>
              ))}
              <th className="py-2 px-2 text-center">#</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => {
              const set = new Set(r.personas || []);
              const universalish = set.size >= 3;
              return (
                <tr key={i} className="border-t border-[var(--line)]" data-testid={`effectiveness-heatmap-row-${i}`}>
                  <td className={`py-2 pr-3 sticky left-0 bg-white text-sm ${universalish ? "font-semibold" : ""}`}>
                    {r.recommendation}
                    <div className="text-[10px] text-[var(--ink-3)] uppercase tracking-wider">{r.area}</div>
                  </td>
                  <td className="py-2 px-2">
                    <PriorityChip priority={r.priority} />
                  </td>
                  {PERSONA_ORDER.map((p) => (
                    <HeatmapCell key={p} active={set.has(p)} priority={r.priority} />
                  ))}
                  <td className="py-2 px-2 text-center text-xs font-mono"
                    style={{ color: universalish ? "var(--accent)" : "var(--ink-3)" }}>
                    {set.size}/5
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="mt-3 text-[11px] text-[var(--ink-3)] flex items-center gap-3 flex-wrap">
        <span>Legend:</span>
        <HeatmapLegend priority="P0" />
        <HeatmapLegend priority="P1" />
        <HeatmapLegend priority="P2" />
        <span>Bold rows appear in 3+ personas (universal).</span>
      </div>
    </Section>
  );
}

function HeatmapCell({ active, priority }) {
  if (!active) {
    return <td className="py-2 px-2 text-center">
      <span className="inline-block w-6 h-6 rounded" style={{ background: "var(--bg-2)" }} />
    </td>;
  }
  const c = PRIORITY_COLOR[priority] || PRIORITY_COLOR.P2;
  return (
    <td className="py-2 px-2 text-center">
      <span className="inline-block w-6 h-6 rounded" style={{ background: c.color, opacity: priority === "P0" ? 1 : priority === "P1" ? 0.75 : 0.5 }} />
    </td>
  );
}

function HeatmapLegend({ priority }) {
  const c = PRIORITY_COLOR[priority] || PRIORITY_COLOR.P2;
  return (
    <span className="inline-flex items-center gap-1">
      <span className="inline-block w-3 h-3 rounded" style={{ background: c.color, opacity: priority === "P0" ? 1 : priority === "P1" ? 0.75 : 0.5 }} />
      <span>{priority}</span>
    </span>
  );
}

function PriorityChip({ priority }) {
  const c = PRIORITY_COLOR[priority] || PRIORITY_COLOR.P2;
  return (
    <span className="pill" style={{ background: c.bg, color: c.color, borderColor: c.color, fontSize: "0.65rem" }}>
      {priority || "P2"}
    </span>
  );
}

function RecIdBadge({ seq }) {
  // Stable, human-readable ID. Falls back to a placeholder for rows that
  // haven't been backfilled yet (next harvest call will fix that — backend
  // backfill is idempotent and runs at the top of every list call).
  const text = typeof seq === "number" ? `REC-${String(seq).padStart(3, "0")}` : "REC-…";
  return (
    <span
      className="pill"
      style={{
        background: "var(--ink, #0F1419)",
        color: "#fff",
        borderColor: "var(--ink, #0F1419)",
        fontSize: "0.65rem",
        fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
        letterSpacing: "0.02em",
        fontWeight: 700,
      }}
      title="Stable recommendation ID — does not change across filters or status updates."
      data-testid={`rec-id-${seq ?? "pending"}`}
    >
      {text}
    </span>
  );
}


function UniversalBlockers({ blockers }) {
  if (!Array.isArray(blockers) || !blockers.length) return null;
  return (
    <Section title="Universal blockers (3+ personas)" icon={<AlertOctagon size={16} />} testid="effectiveness-universal">
      <div className="space-y-2.5">
        {blockers.map((b, i) => {
          const personas = b.personas_affected || [];
          const sev = SEVERITY_COLOR[b.severity] || SEVERITY_COLOR.medium;
          return (
            <div key={i} className="rounded-xl border border-[var(--line)] p-3">
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <span className="pill" style={{ background: sev.bg, color: sev.color, borderColor: sev.color, fontSize: "0.65rem" }}>
                  {(b.severity || "medium").toUpperCase()}
                </span>
                <span className="text-xs text-[var(--accent)] font-semibold">
                  {personas.length}/5 personas
                </span>
                <span className="text-xs text-[var(--ink-3)]">
                  {personas.map((p) => PERSONA_SHORT[p] || p).join(" · ")}
                </span>
              </div>
              <div className="text-sm font-semibold">{b.title}</div>
              <div className="text-sm text-[var(--ink-2)] mt-1">{b.detail}</div>
            </div>
          );
        })}
      </div>
    </Section>
  );
}


function WouldStartComparison({ data }) {
  if (!Array.isArray(data) || !data.length) return null;
  return (
    <Section title="Would each persona start the first challenge?" icon={<Target size={16} />} testid="effectiveness-would-start">
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-2.5">
        {PERSONA_ORDER.map((p) => {
          const row = data.find((d) => d.persona === p) || {};
          const ok = row.would_start === true;
          return (
            <div key={p} className="rounded-xl border p-3"
              style={{
                background: ok ? "rgba(14,159,110,0.05)" : "rgba(255,92,42,0.05)",
                borderColor: ok ? "rgba(14,159,110,0.30)" : "rgba(255,92,42,0.30)",
              }}
            >
              <div className="flex items-center gap-1.5 mb-1">
                {ok ? <CheckCircle2 size={14} className="text-[var(--emerald)]" /> : <XCircle size={14} className="text-[var(--accent)]" />}
                <span className="font-semibold text-sm">{PERSONA_SHORT[p]}</span>
              </div>
              {Array.isArray(row.failing_flags) && row.failing_flags.length > 0 && (
                <div className="text-[11px] text-[var(--ink-3)] leading-snug">
                  Misses: {row.failing_flags.join(", ")}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </Section>
  );
}


function DropOffsByPersona({ data }) {
  if (!Array.isArray(data) || !data.length) return null;
  return (
    <Section title="Biggest drop-off by persona" icon={<TrendingDown size={16} />} testid="effectiveness-batch-dropoffs">
      <div className="space-y-2">
        {data.map((d, i) => (
          <div key={i} className="rounded-xl border border-[var(--line)] p-3">
            <div className="text-xs uppercase tracking-wider text-[var(--ink-3)] font-semibold">{PERSONA_SHORT[d.persona] || d.persona}</div>
            <div className="text-sm font-semibold mt-0.5">{d.moment}</div>
            <div className="text-sm text-[var(--ink-2)] mt-1">{d.why}</div>
          </div>
        ))}
      </div>
    </Section>
  );
}


function PersonaSpecificBlockers({ data }) {
  if (!Array.isArray(data) || !data.length) return null;
  return (
    <Section title="Persona-specific blockers" icon={<AlertTriangle size={16} />} testid="effectiveness-persona-specific">
      <div className="space-y-2">
        {data.map((d, i) => (
          <div key={i} className="rounded-xl border border-[var(--line)] p-3">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xs uppercase tracking-wider text-[var(--ink-3)] font-semibold">{PERSONA_SHORT[d.persona] || d.persona}</span>
            </div>
            <div className="text-sm">{d.blocker}</div>
            {d.moment && <div className="text-xs text-[var(--ink-3)] mt-1">at: {d.moment}</div>}
          </div>
        ))}
      </div>
    </Section>
  );
}


function SharedRecommendations({ recs }) {
  if (!Array.isArray(recs) || !recs.length) return null;
  const sorted = [...recs].sort((a, b) => {
    const rank = { P0: 0, P1: 1, P2: 2 };
    if ((rank[a.priority] ?? 9) !== (rank[b.priority] ?? 9)) return (rank[a.priority] ?? 9) - (rank[b.priority] ?? 9);
    return (b.personas?.length || 0) - (a.personas?.length || 0);
  });
  return (
    <Section title="All shared recommendations" icon={<Sparkles size={16} />} testid="effectiveness-shared-recs">
      <div className="space-y-2.5">
        {sorted.map((r, i) => {
          const personas = r.personas || [];
          return (
            <div key={i} className="rounded-xl border border-[var(--line)] p-3">
              <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                <PriorityChip priority={r.priority} />
                <span className="text-xs text-[var(--ink-3)] uppercase tracking-wider">{r.area}</span>
                <span className="text-xs font-semibold" style={{ color: personas.length >= 3 ? "var(--accent)" : "var(--ink-2)" }}>
                  {personas.length}/5 personas
                </span>
                {r.expected_lift && (
                  <span className="text-xs text-[var(--emerald)] inline-flex items-center gap-1">
                    <TrendingUp size={12} /> {r.expected_lift}
                  </span>
                )}
              </div>
              <div className="text-sm leading-relaxed">{r.change}</div>
              <div className="text-[11px] text-[var(--ink-3)] mt-1">
                {personas.map((p) => PERSONA_SHORT[p] || p).join(" · ")}
              </div>
            </div>
          );
        })}
      </div>
    </Section>
  );
}

// ---------------------------------------------------------------------------
// Recommendation Tracker — closed-loop: review → accept → implement → measure
// ---------------------------------------------------------------------------

const STATUS_META = {
  needs_review: { label: "Needs Review", bg: "rgba(15,15,18,0.06)",  color: "var(--ink-2)",   border: "var(--line)" },
  accepted:     { label: "Accepted",     bg: "rgba(79,70,229,0.10)", color: "var(--indigo)",  border: "rgba(79,70,229,0.30)" },
  rejected:     { label: "Rejected",     bg: "rgba(255,92,42,0.10)", color: "var(--accent)",  border: "rgba(255,92,42,0.30)" },
  implemented:  { label: "Implemented",  bg: "rgba(14,159,110,0.10)",color: "var(--emerald)", border: "rgba(14,159,110,0.30)" },
  auto_staged:  { label: "Auto-Staged for Emergent", bg: "rgba(14,159,110,0.10)", color: "var(--emerald)", border: "rgba(14,159,110,0.40)" },
  staged:       { label: "Staged for Emergent",      bg: "rgba(79,70,229,0.10)",  color: "var(--indigo)",  border: "rgba(79,70,229,0.40)" },
};

const METRIC_LABELS = {
  total_visits: "Total visits",
  start_assessment_clicks: "Start-assessment clicks",
  assessment_starts: "Assessment starts",
  assessment_completions: "Assessment completions",
  first_challenge_starts: "First challenge starts",
  first_challenge_completions: "First challenge completions",
  signup_after_assessment: "Signup after assessment",
  signup_after_first_challenge: "Signup after first challenge",
  paid_conversion_after_first_challenge: "Paid conv. after first challenge",
};

const DEFAULT_IMPACT_AREAS = [
  "first_lesson_starts",
  "assessment_starts",
  "assessment_completions",
  "signup_conversion",
  "paid_conversion",
  "retention",
];

function RecommendationTracker() {
  const [items, setItems] = useState([]);
  const [totals, setTotals] = useState({});
  const [categories, setCategories] = useState([]);
  const [meta, setMeta] = useState({ statuses: [], metrics: [], impact_areas: DEFAULT_IMPACT_AREAS });
  const [loading, setLoading] = useState(true);
  // Filter state — persisted to localStorage so the list does not reset
  // when an admin patches a recommendation and re-renders.
  const [filterStatus, setFilterStatus] = useState(() => loadLS("rec_filter_status", "needs_review"));
  const [filterPriority, setFilterPriority] = useState(() => loadLS("rec_filter_priority", "all"));
  const [filterCategory, setFilterCategory] = useState(() => loadLS("rec_filter_category", "all"));
  const [filterConfidence, setFilterConfidence] = useState(() => loadLS("rec_filter_confidence", "false") === "true");
  const [quickFilter, setQuickFilter] = useState(() => loadLS("rec_quick_filter", "none"));
  const [sortBy, setSortBy] = useState(() => loadLS("rec_sort_by", "default"));
  const [openRecId, setOpenRecId] = useState(null);
  const [error, setError] = useState(null);

  // Persist on every change.
  useEffect(() => { saveLS("rec_filter_status", filterStatus); }, [filterStatus]);
  useEffect(() => { saveLS("rec_filter_priority", filterPriority); }, [filterPriority]);
  useEffect(() => { saveLS("rec_filter_category", filterCategory); }, [filterCategory]);
  useEffect(() => { saveLS("rec_filter_confidence", String(filterConfidence)); }, [filterConfidence]);
  useEffect(() => { saveLS("rec_quick_filter", quickFilter); }, [quickFilter]);
  useEffect(() => { saveLS("rec_sort_by", sortBy); }, [sortBy]);

  // Apply the quick-filter chip on top of the explicit status/priority/HC selectors.
  function applyQuickFilter(qf) {
    setQuickFilter(qf);
    if (qf === "actioned") {
      setFilterStatus("all");          // we client-side filter to actioned set
    } else if (qf === "not_actioned") {
      setFilterStatus("needs_review"); // server-side covers this exactly
    } else if (qf === "high_confidence") {
      setFilterStatus("all");
      setFilterConfidence(true);
    } else if (qf === "p0_only") {
      setFilterStatus("all");
      setFilterPriority("P0");
    }
  }

  async function load() {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterStatus !== "all") params.set("status", filterStatus);
      if (filterPriority !== "all") params.set("priority", filterPriority);
      if (filterCategory !== "all") params.set("category", filterCategory);
      if (filterConfidence) params.set("high_confidence_only", "true");
      const r = await apiClient.get(`/admin/effectiveness/recommendations?${params.toString()}`);
      setItems(r.data.items || []);
      setTotals(r.data.totals || {});
      setCategories(r.data.categories || []);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }

  async function loadMeta() {
    try {
      const r = await apiClient.get("/admin/effectiveness/recommendations-meta/options");
      setMeta({ ...r.data, impact_areas: r.data.impact_areas || DEFAULT_IMPACT_AREAS });
    } catch (e) { /* non-fatal */ }
  }

  useEffect(() => { load(); }, [filterStatus, filterPriority, filterCategory, filterConfidence]);
  useEffect(() => { loadMeta(); }, []);

  async function harvestNow() {
    try {
      const r = await apiClient.post("/admin/effectiveness/recommendations/harvest");
      if (r.data?.newly_inserted) toastSafe(`+${r.data.newly_inserted} new items harvested`);
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    }
  }

  const implementedItems = useMemo(
    () => items.filter((i) => i.status === "implemented"),
    [items],
  );

  // Apply the "Actioned" quick-filter client-side (server can't filter by
  // a multi-status set in a single call). Other quick filters are mirrored
  // into the explicit status/priority/HC selectors in `applyQuickFilter`.
  const ACTIONED_STATUSES = ["accepted", "rejected", "implemented", "auto_staged", "staged"];
  const TIER_RANK = { "Very High": 3, "High": 2, "Medium": 1, "Low": 0 };
  const COMPLEXITY_RANK = { "Low": 0, "Medium": 1, "High": 2 };
  const CONFIDENCE_RANK = { "High": 2, "Medium": 1, "Low": 0 };
  const displayItems = useMemo(() => {
    let arr = items;
    if (quickFilter === "actioned") {
      arr = arr.filter((i) => ACTIONED_STATUSES.includes(i.status));
    }
    // Best-next-fix sort key — used as a tie-breaker so "Best Next Fix" items
    // float to the top of every sort mode.
    const bnf = (it) => (it.projected_impact?.best_next_fix ? 1 : 0);
    const cmp = {
      default: null, // keep server order
      highest_impact: (a, b) =>
        (b.projected_impact?.impact_score ?? 0) - (a.projected_impact?.impact_score ?? 0),
      highest_conversion_impact: (a, b) =>
        (TIER_RANK[b.projected_impact?.conversion_impact] ?? -1) -
        (TIER_RANK[a.projected_impact?.conversion_impact] ?? -1),
      highest_activity_impact: (a, b) =>
        (TIER_RANK[b.projected_impact?.user_activity_impact] ?? -1) -
        (TIER_RANK[a.projected_impact?.user_activity_impact] ?? -1),
      lowest_complexity: (a, b) =>
        (COMPLEXITY_RANK[a.projected_impact?.complexity] ?? 99) -
        (COMPLEXITY_RANK[b.projected_impact?.complexity] ?? 99),
      highest_confidence: (a, b) =>
        (CONFIDENCE_RANK[b.projected_impact?.confidence] ?? -1) -
        (CONFIDENCE_RANK[a.projected_impact?.confidence] ?? -1),
    }[sortBy];
    if (cmp) {
      arr = [...arr].sort((a, b) => {
        // Best Next Fix wins ties.
        const bnfDiff = bnf(b) - bnf(a);
        if (bnfDiff !== 0) return bnfDiff;
        const primary = cmp(a, b);
        if (primary !== 0) return primary;
        // Fallback tie-breaker: highest impact score.
        return (b.projected_impact?.impact_score ?? 0) - (a.projected_impact?.impact_score ?? 0);
      });
    }
    return arr;
  }, [items, quickFilter, sortBy]);

  return (
    <div className="space-y-4" data-testid="recommendation-tracker">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="font-display text-2xl tracking-tight flex items-center gap-2">
            <ClipboardList size={18} /> Recommendation Tracker
          </h2>
          <p className="text-xs text-[var(--ink-3)] mt-1 max-w-2xl">
            Closed-loop tracker for every recommendation surfaced by the Lab — review → accept → implement → measure. High-Confidence Fix badge = P0 across 3+ personas.
          </p>
        </div>
        <button
          type="button"
          className="btn-ghost"
          onClick={harvestNow}
          data-testid="rec-tracker-harvest-btn"
          title="Re-scan latest reports & batches for new recommendations"
        >
          <RefreshCcw size={14} /> Refresh
        </button>
        <button
          type="button"
          className="btn-primary"
          onClick={async () => {
            if (!window.confirm("Auto-stage every Needs-Review recommendation that meets the strict high-confidence + low-risk criteria? Risky/ambiguous items will be left for manual review.")) return;
            try {
              const r = await apiClient.post("/admin/effectiveness/recommendations/auto-stage-all");
              window.alert(`${r.data?.staged ?? 0} recommendation(s) auto-staged. ${r.data?.scanned ?? 0} scanned.`);
              await load();
            } catch (e) {
              setError(e?.response?.data?.detail || e.message);
            }
          }}
          data-testid="rec-tracker-autostage-btn"
          title="Sweep Needs-Review items; auto-stage only those that pass every safety check"
        >
          <Zap size={14} /> Auto-Stage Eligible
        </button>
      </div>

      <TopRequiringApproval items={items} onChange={load} />

      <TrackerStatusStrip totals={totals} active={filterStatus} onChange={setFilterStatus} />

      <QuickFilterChips active={quickFilter} onChange={applyQuickFilter} totals={totals} />

      <div className="flex flex-wrap items-center gap-2 text-xs">
        <FilterPill label="Priority" value={filterPriority} options={[
          { id: "all", label: "All" },
          { id: "P0", label: "P0" },
          { id: "P1", label: "P1" },
          { id: "P2", label: "P2" },
        ]} onChange={setFilterPriority} testid="rec-tracker-priority" />
        <FilterPill label="Category" value={filterCategory} options={[
          { id: "all", label: "All" },
          ...categories.map((c) => ({ id: c, label: c })),
        ]} onChange={setFilterCategory} testid="rec-tracker-category" />
        <button
          type="button"
          onClick={() => setFilterConfidence((v) => !v)}
          className="pill"
          style={{
            background: filterConfidence ? "rgba(255,92,42,0.10)" : "var(--bg-2)",
            color: filterConfidence ? "var(--accent)" : "var(--ink-2)",
            borderColor: filterConfidence ? "rgba(255,92,42,0.30)" : "var(--line)",
            cursor: "pointer",
            fontSize: "0.7rem",
          }}
          data-testid="rec-tracker-hc-toggle"
        >
          <Zap size={11} className="inline" /> High-Confidence Fixes only
        </button>
        <FilterPill label={<><ArrowUpDown size={11} className="inline mr-1" />Sort</>} value={sortBy} options={[
          { id: "default", label: "Server order" },
          { id: "highest_impact", label: "Highest projected impact" },
          { id: "highest_conversion_impact", label: "Highest conversion impact" },
          { id: "highest_activity_impact", label: "Highest user-activity impact" },
          { id: "lowest_complexity", label: "Lowest implementation complexity" },
          { id: "highest_confidence", label: "Highest confidence" },
        ]} onChange={setSortBy} testid="rec-tracker-sort" />
      </div>

      {error && (
        <div className="text-sm rounded-lg px-3 py-2"
          style={{ background: "rgba(255,92,42,0.08)", color: "var(--accent)" }}
          data-testid="rec-tracker-error"
        >
          {error}
        </div>
      )}

      {loading ? (
        <div className="brut p-6 text-center text-sm text-[var(--ink-3)]">
          <span className="spinner mr-2" /> Loading…
        </div>
      ) : displayItems.length === 0 ? (
        <div className="brut p-6 text-center text-sm text-[var(--ink-3)]">
          No recommendations match these filters yet. Run a simulation above to generate items.
        </div>
      ) : (
        <div className="space-y-2.5">
          {displayItems.map((it) => (
            <RecommendationRow
              key={it.rec_id}
              item={it}
              expanded={openRecId === it.rec_id}
              onToggle={() => setOpenRecId(openRecId === it.rec_id ? null : it.rec_id)}
              onChanged={load}
              impactAreas={meta.impact_areas}
              metrics={meta.metrics}
            />
          ))}
        </div>
      )}

      {implementedItems.length > 0 && (
        <ImplementedFixImpact items={implementedItems} onChanged={load} />
      )}
    </div>
  );
}

function toastSafe(_msg) {
  // No-op fallback. Sonner is mounted elsewhere; if we need a toast here
  // later, import it at the top of this file.
}

function FilterPill({ label, value, options, onChange, testid }) {
  return (
    <label className="inline-flex items-center gap-1.5">
      <span className="text-[var(--ink-3)] uppercase tracking-wider font-semibold">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="input-base text-xs"
        style={{ padding: "4px 8px", height: "auto" }}
        data-testid={testid}
      >
        {options.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
      </select>
    </label>
  );
}

function TrackerStatusStrip({ totals, active, onChange }) {
  // Order matches the user's spec: Needs Review first (largest queue),
  // then the staging pipeline statuses, then terminal outcomes.
  const order = ["needs_review", "auto_staged", "staged", "accepted", "implemented", "rejected"];
  return (
    <div
      className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2"
      data-testid="rec-tracker-status-strip"
    >
      <StatusChip
        label="All"
        count={totals?.all ?? Object.values(totals || {}).filter((v) => typeof v === "number").reduce((a, b) => a + b, 0)}
        active={active === "all"}
        onClick={() => onChange("all")}
        tone={{ color: "var(--ink-2)", bg: "var(--bg-2)", border: "var(--line)" }}
        testid="rec-status-all"
      />
      {order.map((s) => (
        <StatusChip
          key={s}
          label={STATUS_META[s].label}
          count={totals?.[s] || 0}
          active={active === s}
          onClick={() => onChange(s)}
          tone={STATUS_META[s]}
          testid={`rec-status-${s}`}
        />
      ))}
    </div>
  );
}

function QuickFilterChips({ active, onChange, totals }) {
  const chips = [
    {
      id: "actioned",
      label: "Actioned",
      count: totals?.actioned || 0,
      tone: { color: "var(--emerald)", bg: "rgba(14,159,110,0.10)", border: "rgba(14,159,110,0.30)" },
    },
    {
      id: "not_actioned",
      label: "Not Actioned",
      count: totals?.not_actioned || 0,
      tone: { color: "var(--ink-2)", bg: "rgba(15,15,18,0.06)", border: "var(--line)" },
    },
    {
      id: "high_confidence",
      label: "High-Confidence",
      count: totals?.high_confidence || 0,
      tone: { color: "var(--accent)", bg: "rgba(255,92,42,0.10)", border: "rgba(255,92,42,0.30)" },
    },
    {
      id: "p0_only",
      label: "P0 Only",
      count: totals?.p0_only || 0,
      tone: { color: "var(--accent)", bg: "rgba(255,92,42,0.10)", border: "rgba(255,92,42,0.35)" },
    },
  ];
  return (
    <div
      className="flex flex-wrap items-center gap-2"
      data-testid="rec-tracker-quickfilters"
    >
      <span className="text-[10px] uppercase tracking-wider font-semibold text-[var(--ink-3)] mr-1">
        Quick filter
      </span>
      {chips.map((c) => {
        const isActive = active === c.id;
        return (
          <button
            key={c.id}
            type="button"
            onClick={() => onChange(isActive ? "none" : c.id)}
            className="pill"
            style={{
              background: isActive ? c.tone.color : c.tone.bg,
              color: isActive ? "#fff" : c.tone.color,
              borderColor: isActive ? c.tone.color : c.tone.border,
              cursor: "pointer",
              fontSize: "0.72rem",
            }}
            data-testid={`rec-quickfilter-${c.id}`}
          >
            {c.label} <span className="opacity-80">({c.count})</span>
          </button>
        );
      })}
    </div>
  );
}

function StatusChip({ label, count, active, onClick, tone, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="rounded-xl px-3 py-2.5 text-left transition-all"
      style={{
        background: active ? tone.color : tone.bg,
        color: active ? "#fff" : tone.color,
        border: `1px solid ${active ? tone.color : tone.border}`,
        boxShadow: active ? "0 2px 8px rgba(0,0,0,0.08)" : "none",
      }}
      data-testid={testid}
    >
      <div className="text-[10px] uppercase tracking-wider font-semibold opacity-80">{label}</div>
      <div className="text-xl font-display mt-0.5">{count}</div>
    </button>
  );
}

function ImpactPill({ label, value, tone, testid }) {
  return (
    <span
      className="pill inline-flex items-center gap-1"
      style={{
        background: tone.bg,
        color: tone.color,
        borderColor: tone.border,
        fontSize: "0.62rem",
        fontWeight: 600,
        letterSpacing: "0.02em",
      }}
      data-testid={testid}
    >
      <span className="opacity-70 uppercase tracking-wider">{label}</span>
      <span className="font-semibold">{value}</span>
    </span>
  );
}

function ProjectedImpactStrip({ pi, recId }) {
  if (!pi) return null;
  const TIER_TONE = {
    "Very High": { bg: "rgba(14,159,110,0.12)", color: "var(--emerald)", border: "rgba(14,159,110,0.35)" },
    "High":      { bg: "rgba(79,70,229,0.10)",  color: "var(--indigo)",  border: "rgba(79,70,229,0.30)" },
    "Medium":    { bg: "rgba(234,179,8,0.10)",  color: "#a16207",        border: "rgba(234,179,8,0.30)" },
    "Low":       { bg: "var(--bg-2)",           color: "var(--ink-3)",   border: "var(--line)" },
  };
  const COMPLEXITY_TONE = {
    "Low":    { bg: "rgba(14,159,110,0.10)", color: "var(--emerald)", border: "rgba(14,159,110,0.30)" },
    "Medium": { bg: "rgba(234,179,8,0.10)",  color: "#a16207",        border: "rgba(234,179,8,0.30)" },
    "High":   { bg: "rgba(255,92,42,0.10)",  color: "var(--accent)",  border: "rgba(255,92,42,0.30)" },
  };
  const CONF_TONE = {
    "High":   { bg: "rgba(14,159,110,0.10)", color: "var(--emerald)", border: "rgba(14,159,110,0.30)" },
    "Medium": { bg: "rgba(234,179,8,0.10)",  color: "#a16207",        border: "rgba(234,179,8,0.30)" },
    "Low":    { bg: "var(--bg-2)",           color: "var(--ink-3)",   border: "var(--line)" },
  };
  return (
    <div
      className="mt-2.5 pt-2.5 border-t border-dashed border-[var(--line)] space-y-1.5"
      data-testid={`rec-projected-impact-${recId}`}
    >
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-[var(--ink-3)] font-semibold">
        <Sparkles size={11} className="text-[var(--indigo)]" />
        Projected impact
        <span className="ml-1 pill" style={{
          background: "rgba(79,70,229,0.10)", color: "var(--indigo)",
          borderColor: "rgba(79,70,229,0.30)", fontSize: "0.62rem", fontWeight: 700,
        }} data-testid={`rec-impact-score-${recId}`}>
          Score {pi.impact_score}/10
        </span>
      </div>
      <div className="flex flex-wrap gap-1.5">
        <ImpactPill label="Business"   value={pi.business_impact}      tone={TIER_TONE[pi.business_impact] || TIER_TONE.Low}      testid={`rec-impact-business-${recId}`} />
        <ImpactPill label="Activity"   value={pi.user_activity_impact} tone={TIER_TONE[pi.user_activity_impact] || TIER_TONE.Low} testid={`rec-impact-activity-${recId}`} />
        <ImpactPill label="Conversion" value={pi.conversion_impact}    tone={TIER_TONE[pi.conversion_impact] || TIER_TONE.Low}    testid={`rec-impact-conversion-${recId}`} />
        <ImpactPill label="Complexity" value={pi.complexity}           tone={COMPLEXITY_TONE[pi.complexity] || COMPLEXITY_TONE.High} testid={`rec-impact-complexity-${recId}`} />
        <ImpactPill label="Confidence" value={pi.confidence}           tone={CONF_TONE[pi.confidence] || CONF_TONE.Low}            testid={`rec-impact-confidence-${recId}`} />
        <ImpactPill label="Metric"     value={pi.expected_metric}      tone={{ bg: "var(--bg-2)", color: "var(--ink-2)", border: "var(--line)" }} testid={`rec-impact-metric-${recId}`} />
      </div>
      {pi.rationale && (
        <div className="text-[11px] text-[var(--ink-3)] leading-snug italic" data-testid={`rec-impact-rationale-${recId}`}>
          {pi.rationale}
        </div>
      )}
    </div>
  );
}

function RecommendationRow({ item, expanded, onToggle, onChanged, impactAreas }) {
  const statusTone = STATUS_META[item.status] || STATUS_META.needs_review;
  return (
    <div
      className="brut p-4 transition-colors"
      style={expanded ? { background: "rgba(79,70,229,0.04)" } : undefined}
      data-testid={`rec-row-${item.rec_id}`}
    >
      <button
        type="button"
        onClick={onToggle}
        className="w-full text-left"
        data-testid={`rec-row-toggle-${item.rec_id}`}
      >
        <div className="flex items-start gap-2 flex-wrap">
          <RecIdBadge seq={item.display_seq} />
          <PriorityChip priority={item.priority} />
          <span className="pill"
            style={{
              background: statusTone.bg,
              color: statusTone.color,
              borderColor: statusTone.border,
              fontSize: "0.65rem",
            }}
          >
            {statusTone.label}
          </span>
          <span className="text-xs text-[var(--ink-3)] uppercase tracking-wider">{item.category}</span>
          {item.high_confidence && (
            <span className="pill inline-flex items-center gap-1"
              style={{
                background: "rgba(255,92,42,0.12)",
                color: "var(--accent)",
                borderColor: "rgba(255,92,42,0.40)",
                fontSize: "0.65rem",
                fontWeight: 700,
              }}
              data-testid={`rec-hc-badge-${item.rec_id}`}
              title="P0 across 3+ personas — universal blocker"
            >
              <Zap size={11} /> HIGH-CONFIDENCE FIX
            </span>
          )}
          {item.auto_eligible && item.status === "needs_review" && (
            <span className="pill" title={(item.qualification_reasons||[]).join(' · ')}
              style={{ background: "rgba(14,159,110,0.10)", color: "var(--emerald)", borderColor: "rgba(14,159,110,0.40)", fontSize: "0.6rem", fontWeight: 700 }}
              data-testid={`rec-auto-eligible-${item.rec_id}`}>
              AUTO-ELIGIBLE
            </span>
          )}
          {!item.auto_eligible && Array.isArray(item.risk_reasons) && item.risk_reasons.length > 0 && item.status === "needs_review" && (
            <span className="pill" title={item.risk_reasons.join(' · ')}
              style={{ background: "rgba(234,179,8,0.10)", color: "#a16207", borderColor: "rgba(234,179,8,0.30)", fontSize: "0.6rem", fontWeight: 700 }}
              data-testid={`rec-needs-review-badge-${item.rec_id}`}>
              NEEDS REVIEW
            </span>
          )}
          {item.projected_impact?.best_next_fix && (
            <span className="pill inline-flex items-center gap-1"
              style={{
                background: "linear-gradient(135deg,#0ea5e9 0%,#6366f1 100%)",
                color: "#fff",
                borderColor: "transparent",
                fontSize: "0.65rem",
                fontWeight: 700,
                letterSpacing: "0.04em",
              }}
              data-testid={`rec-best-next-fix-${item.rec_id}`}
              title="P0 + High/Very-High projected impact + Low/Medium complexity + High confidence"
            >
              <Sparkles size={11} /> BEST NEXT FIX
            </span>
          )}
          {item.num_personas_affected > 0 && (
            <span className="text-xs text-[var(--ink-2)]">
              {item.num_personas_affected}/5 personas
            </span>
          )}
          {item.source_type === "batch" && (
            <span className="text-[10px] text-[var(--ink-3)]">Batch · {item.depth}</span>
          )}
          {item.source_type === "report" && item.persona && (
            <span className="text-[10px] text-[var(--ink-3)]">Single · {PERSONA_SHORT[item.persona] || item.persona}</span>
          )}
        </div>
        <div className="text-sm mt-2 leading-relaxed">{item.change}</div>
        {item.expected_lift && (
          <div className="text-xs text-[var(--emerald)] mt-1 inline-flex items-center gap-1">
            <TrendingUp size={11} /> {item.expected_lift}
          </div>
        )}
        {item.projected_impact && <ProjectedImpactStrip pi={item.projected_impact} recId={item.rec_id} />}
      </button>

      {expanded && (
        <RecommendationActions item={item} onChanged={onChanged} impactAreas={impactAreas} />
      )}
    </div>
  );
}

function RecommendationActions({ item, onChanged, impactAreas }) {
  const [notes, setNotes] = useState("");
  const [rejReason, setRejReason] = useState("");
  const [implNote, setImplNote] = useState("");
  const [relSummary, setRelSummary] = useState("");
  const [impactArea, setImpactArea] = useState(item.expected_impact_area || "first_lesson_starts");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  async function patch(payload) {
    setBusy(true);
    setErr(null);
    try {
      await apiClient.patch(`/admin/effectiveness/recommendations/${item.rec_id}`, payload);
      setNotes("");
      setRejReason("");
      setImplNote("");
      setRelSummary("");
      await onChanged();
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mt-4 pt-4 border-t border-[var(--line)] space-y-4" data-testid={`rec-actions-${item.rec_id}`}>
      {item.notes && (
        <div className="rounded-lg p-3 text-xs whitespace-pre-wrap" style={{ background: "var(--bg-2)" }}>
          <div className="uppercase tracking-wider font-semibold text-[var(--ink-3)] mb-1">Notes history</div>
          {item.notes}
        </div>
      )}

      {item.status !== "implemented" && (
        <div>
          <div className="text-xs uppercase tracking-wider font-semibold text-[var(--ink-3)] mb-1.5">Add note (optional)</div>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Why you're accepting / context for the team…"
            className="input-base w-full text-sm"
            rows={2}
            data-testid={`rec-notes-${item.rec_id}`}
          />
        </div>
      )}

      {item.status !== "implemented" && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          <button
            type="button"
            disabled={busy}
            onClick={() => patch({ status: "accepted", notes: notes || undefined, expected_impact_area: impactArea })}
            className="btn-ghost justify-center"
            style={{ borderColor: STATUS_META.accepted.border, color: STATUS_META.accepted.color }}
            data-testid={`rec-accept-${item.rec_id}`}
          >
            <Check size={14} /> Accept
          </button>
          <button
            type="button"
            disabled={busy || !rejReason.trim()}
            onClick={() => patch({ status: "rejected", notes: notes || undefined, rejection_reason: rejReason })}
            className="btn-ghost justify-center"
            style={{ borderColor: STATUS_META.rejected.border, color: STATUS_META.rejected.color }}
            data-testid={`rec-reject-${item.rec_id}`}
            title={rejReason.trim() ? "Mark as rejected" : "Add a rejection reason below first"}
          >
            <X size={14} /> Reject
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => patch({
              status: "implemented",
              notes: notes || undefined,
              implementation_note: implNote || undefined,
              related_change_summary: relSummary || undefined,
              expected_impact_area: impactArea,
            })}
            className="btn-primary justify-center"
            data-testid={`rec-implement-${item.rec_id}`}
          >
            <ArrowRight size={14} /> Mark as Implemented
          </button>
        </div>
      )}

      {item.status !== "implemented" && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
          <label className="block">
            <span className="block uppercase tracking-wider text-[var(--ink-3)] font-semibold mb-1">Rejection reason (required to reject)</span>
            <input
              value={rejReason}
              onChange={(e) => setRejReason(e.target.value)}
              placeholder="e.g. Already covered by item #X"
              className="input-base w-full"
              data-testid={`rec-rej-reason-${item.rec_id}`}
            />
          </label>
          <label className="block">
            <span className="block uppercase tracking-wider text-[var(--ink-3)] font-semibold mb-1">Expected impact area</span>
            <select
              value={impactArea}
              onChange={(e) => setImpactArea(e.target.value)}
              className="input-base w-full"
              data-testid={`rec-impact-area-${item.rec_id}`}
            >
              {impactAreas.map((ia) => <option key={ia} value={ia}>{ia.replace(/_/g, " ")}</option>)}
            </select>
          </label>
        </div>
      )}

      {item.status !== "implemented" && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
          <label className="block">
            <span className="block uppercase tracking-wider text-[var(--ink-3)] font-semibold mb-1">Implementation note (when marking implemented)</span>
            <input
              value={implNote}
              onChange={(e) => setImplNote(e.target.value)}
              placeholder="e.g. Hero copy v2 shipped 6/24"
              className="input-base w-full"
              data-testid={`rec-impl-note-${item.rec_id}`}
            />
          </label>
          <label className="block">
            <span className="block uppercase tracking-wider text-[var(--ink-3)] font-semibold mb-1">Related change summary</span>
            <input
              value={relSummary}
              onChange={(e) => setRelSummary(e.target.value)}
              placeholder="e.g. Landing.jsx hero + microcopy"
              className="input-base w-full"
              data-testid={`rec-rel-summary-${item.rec_id}`}
            />
          </label>
        </div>
      )}

      {item.status === "implemented" && (
        <ImplementedDetails item={item} />
      )}

      {err && (
        <div className="text-xs rounded-lg px-3 py-2"
          style={{ background: "rgba(255,92,42,0.08)", color: "var(--accent)" }}
        >
          {err}
        </div>
      )}

      <MetricEditor item={item} onChanged={onChanged} />

      <StagePanel item={item} onChanged={onChanged} />

      <SnapshotPanel item={item} onChanged={onChanged} />

      {item.status === "implemented" && (
        <button
          type="button"
          className="btn-ghost text-xs"
          onClick={() => patch({ status: "accepted", notes: "Reopened from Implemented" })}
          data-testid={`rec-reopen-${item.rec_id}`}
        >
          Reopen (move back to Accepted)
        </button>
      )}
    </div>
  );
}

function ImplementedDetails({ item }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
      <DetailBox label="Implemented at" value={item.implemented_at ? new Date(item.implemented_at).toLocaleString() : "—"} />
      <DetailBox label="Expected impact" value={(item.expected_impact_area || "—").replace(/_/g, " ")} />
      {item.implementation_note && <DetailBox label="Implementation note" value={item.implementation_note} />}
      {item.related_change_summary && <DetailBox label="Related change" value={item.related_change_summary} />}
    </div>
  );
}

function DetailBox({ label, value }) {
  return (
    <div className="rounded-lg p-2.5" style={{ background: "var(--bg-2)" }}>
      <div className="uppercase tracking-wider text-[var(--ink-3)] font-semibold mb-0.5">{label}</div>
      <div className="text-[var(--ink)]">{value}</div>
    </div>
  );
}

function MetricEditor({ item, onChanged }) {
  const [metric, setMetric] = useState("first_challenge_starts");
  const [before, setBefore] = useState("");
  const [after, setAfter] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  async function save() {
    setBusy(true);
    setErr(null);
    try {
      const payload = { metric };
      if (before !== "") payload.before = parseFloat(before);
      if (after !== "") payload.after = parseFloat(after);
      if (payload.before === undefined && payload.after === undefined) {
        setErr("Enter a before or after value");
        setBusy(false);
        return;
      }
      await apiClient.post(`/admin/effectiveness/recommendations/${item.rec_id}/metrics`, payload);
      setBefore("");
      setAfter("");
      await onChanged();
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  }

  async function clearMetric(m) {
    if (!window.confirm(`Clear ${METRIC_LABELS[m] || m}?`)) return;
    try {
      await apiClient.delete(`/admin/effectiveness/recommendations/${item.rec_id}/metrics/${m}`);
      await onChanged();
    } catch (e) { setErr(e?.response?.data?.detail || e.message); }
  }

  const metrics = item.metrics || {};
  const filledKeys = Object.keys(metrics);

  return (
    <div className="rounded-xl p-3" style={{ background: "var(--bg-2)" }}>
      <div className="flex items-center gap-2 mb-2">
        <TrendingUp size={13} className="text-[var(--ink-3)]" />
        <span className="text-xs uppercase tracking-wider font-semibold text-[var(--ink-3)]">Conversion metrics — before / after</span>
      </div>

      {filledKeys.length > 0 ? (
        <div className="space-y-1.5 mb-3">
          {filledKeys.map((m) => {
            const v = metrics[m] || {};
            return (
              <MetricDeltaRow
                key={m}
                metric={m}
                before={v.before}
                after={v.after}
                onClear={() => clearMetric(m)}
              />
            );
          })}
        </div>
      ) : (
        <div className="text-xs text-[var(--ink-3)] mb-2">No metrics yet.</div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 items-end">
        <label className="block text-xs sm:col-span-2">
          <span className="block uppercase tracking-wider text-[var(--ink-3)] font-semibold mb-1">Metric</span>
          <select
            value={metric}
            onChange={(e) => setMetric(e.target.value)}
            className="input-base w-full text-xs"
            data-testid={`rec-metric-select-${item.rec_id}`}
          >
            {Object.entries(METRIC_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </label>
        <label className="block text-xs">
          <span className="block uppercase tracking-wider text-[var(--ink-3)] font-semibold mb-1">Before</span>
          <input type="number" inputMode="decimal" value={before} onChange={(e) => setBefore(e.target.value)} className="input-base w-full" placeholder="0" data-testid={`rec-metric-before-${item.rec_id}`} />
        </label>
        <label className="block text-xs">
          <span className="block uppercase tracking-wider text-[var(--ink-3)] font-semibold mb-1">After</span>
          <input type="number" inputMode="decimal" value={after} onChange={(e) => setAfter(e.target.value)} className="input-base w-full" placeholder="12" data-testid={`rec-metric-after-${item.rec_id}`} />
        </label>
      </div>
      <div className="mt-2">
        <button type="button" onClick={save} disabled={busy} className="btn-ghost text-xs" data-testid={`rec-metric-save-${item.rec_id}`}>
          {busy ? "Saving…" : "Save metric"}
        </button>
        {err && <span className="ml-2 text-xs text-[var(--accent)]">{err}</span>}
      </div>
    </div>
  );
}

function MetricDeltaRow({ metric, before, after, onClear, sourceLabel }) {
  const hasBefore = typeof before === "number";
  const hasAfter = typeof after === "number";
  const delta = (hasBefore && hasAfter) ? (after - before) : null;
  const pct = (hasBefore && hasAfter && before !== 0) ? ((after - before) / before) * 100 : (hasAfter && before === 0 && after !== 0 ? Infinity : null);
  const trend = delta == null ? "flat" : delta > 0 ? "up" : delta < 0 ? "down" : "flat";
  const color = trend === "up" ? "var(--emerald)" : trend === "down" ? "var(--accent)" : "var(--ink-3)";
  const srcTone = sourceLabel === "auto" ? "var(--emerald)" : sourceLabel === "manual" ? "var(--ink-3)" : "var(--indigo)";
  return (
    <div className="flex items-center justify-between gap-3 text-xs rounded-lg px-2.5 py-1.5 bg-white border border-[var(--line)]">
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-[var(--ink)] truncate flex items-center gap-1.5">
          {METRIC_LABELS[metric] || metric}
          {sourceLabel && (
            <span className="pill" style={{
              background: "var(--bg-2)", color: srcTone, borderColor: "var(--line)",
              fontSize: "0.55rem", padding: "0px 5px", textTransform: "uppercase", letterSpacing: "0.05em",
            }}>
              {sourceLabel}
            </span>
          )}
        </div>
        <div className="text-[var(--ink-3)]">
          {hasBefore ? before : "—"} → {hasAfter ? after : "—"}
        </div>
      </div>
      <div className="font-mono text-sm" style={{ color }}>
        {delta == null ? "—" : (delta > 0 ? `+${delta}` : `${delta}`)}
        {pct != null && Number.isFinite(pct) && (
          <span className="text-[10px] ml-1 text-[var(--ink-3)]">
            ({pct > 0 ? "+" : ""}{pct.toFixed(0)}%)
          </span>
        )}
        {pct === Infinity && (
          <span className="text-[10px] ml-1 text-[var(--ink-3)]">(0 → {after})</span>
        )}
      </div>
      <div className="font-display" style={{ color }}>
        {trend === "up" ? <TrendingUp size={14} /> : trend === "down" ? <TrendingDown size={14} /> : "—"}
      </div>
      {onClear && (
        <button type="button" onClick={onClear} className="text-[var(--ink-3)] hover:text-[var(--accent)] p-1" aria-label="Clear metric">
          <Trash2 size={12} />
        </button>
      )}
    </div>
  );
}


function ImplementedFixImpact({ items, onChanged }) {
  if (!items.length) return null;
  return (
    <Section title="Implemented Fix Impact" icon={<Award size={16} />} testid="rec-impact-view">
      <p className="text-xs text-[var(--ink-3)] mb-3">
        Every shipped fix and whether it moved the metric it was supposed to move. Auto-snapshot values take priority; manual entries fill any gaps.
      </p>
      <div className="space-y-3">
        {items.map((it) => {
          const merged = mergeImpactMetrics(it);
          return (
            <div key={it.rec_id} className="rounded-xl border border-[var(--line)] p-3.5" data-testid={`impact-card-${it.rec_id}`}>
              <div className="flex items-center gap-2 flex-wrap mb-1.5">
                <RecIdBadge seq={it.display_seq} />
                <PriorityChip priority={it.priority} />
                <span className="text-xs text-[var(--ink-3)] uppercase tracking-wider">{it.category}</span>
                {it.high_confidence && (
                  <span className="pill inline-flex items-center gap-1"
                    style={{ background: "rgba(255,92,42,0.12)", color: "var(--accent)", borderColor: "rgba(255,92,42,0.40)", fontSize: "0.65rem", fontWeight: 700 }}
                  >
                    <Zap size={11} /> HIGH-CONFIDENCE
                  </span>
                )}
                <span className="text-xs text-[var(--ink-3)]">
                  Impl {it.implemented_at ? new Date(it.implemented_at).toLocaleDateString() : "—"}
                </span>
                <ImpactSourceStrip before={it.before_snapshot} after={it.after_snapshot} hasManual={Object.keys(it.metrics || {}).length > 0} />
              </div>
              <div className="text-sm font-semibold mb-1">{it.change}</div>
              {it.related_change_summary && (
                <div className="text-xs text-[var(--ink-2)] mb-2 italic">Shipped: {it.related_change_summary}</div>
              )}
              {merged.length === 0 ? (
                <div className="text-xs text-[var(--ink-3)]">No metrics tracked yet — expand the row above to capture snapshots or enter values manually.</div>
              ) : (
                <div className="space-y-1.5">
                  {merged.map((row) => (
                    <MetricDeltaRow
                      key={row.metric}
                      metric={row.metric}
                      before={row.before}
                      after={row.after}
                      sourceLabel={row.sourceLabel}
                      onClear={() => {}}
                    />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </Section>
  );
}

function ImpactSourceStrip({ before, after, hasManual }) {
  const pills = [];
  if (before) pills.push({ k: "before", label: "Before snapshot", color: "var(--indigo)" });
  if (after) pills.push({ k: "after", label: "After snapshot", color: "var(--emerald)" });
  if (hasManual) pills.push({ k: "manual", label: "Manual entries", color: "var(--ink-2)" });
  if (!pills.length) return null;
  return (
    <span className="flex items-center gap-1 ml-1">
      {pills.map((p) => (
        <span key={p.k} className="pill" style={{
          background: "var(--bg-2)", color: p.color, borderColor: "var(--line)", fontSize: "0.6rem", padding: "1px 6px",
        }}>
          {p.label}
        </span>
      ))}
    </span>
  );
}

// Merge snapshot + manual metric values. Auto-snapshot data wins per-metric;
// manual fills in gaps.
function mergeImpactMetrics(item) {
  const before = item.before_snapshot?.metrics || {};
  const after = item.after_snapshot?.metrics || {};
  const manual = item.metrics || {};
  const keys = new Set([
    ...Object.keys(before),
    ...Object.keys(after),
    ...Object.keys(manual),
  ]);
  const rows = [];
  for (const m of keys) {
    const autoBefore = before[m]?.source === "auto_mongo" ? before[m]?.value : null;
    const autoAfter = after[m]?.source === "auto_mongo" ? after[m]?.value : null;
    const manualBefore = typeof manual[m]?.before === "number" ? manual[m].before : null;
    const manualAfter = typeof manual[m]?.after === "number" ? manual[m].after : null;
    const b = autoBefore != null ? autoBefore : manualBefore;
    const a = autoAfter != null ? autoAfter : manualAfter;
    if (b == null && a == null) continue;
    let src;
    if (autoBefore != null && autoAfter != null) src = "auto";
    else if (manualBefore != null && manualAfter != null) src = "manual";
    else src = "mixed";
    rows.push({
      metric: m,
      before: b == null ? undefined : b,
      after: a == null ? undefined : a,
      sourceLabel: src,
    });
  }
  // Prioritize visitor→first-lesson metrics at the top
  const order = [
    "total_visits",
    "assessment_starts",
    "assessment_completions",
    "first_challenge_starts",
    "first_challenge_completions",
    "signup_after_first_challenge",
    "paid_conversion_after_first_challenge",
    "start_assessment_clicks",
    "signup_after_assessment",
  ];
  rows.sort((x, y) => order.indexOf(x.metric) - order.indexOf(y.metric));
  return rows;
}

// ---------------------------------------------------------------------------
// Snapshot panel — Before / After auto-capture from existing analytics
// ---------------------------------------------------------------------------

function SnapshotPanel({ item, onChanged }) {
  const [windowDays, setWindowDays] = useState(14);
  const [noteBefore, setNoteBefore] = useState("");
  const [noteAfter, setNoteAfter] = useState("");
  const [busy, setBusy] = useState(null); // "before" | "after" | "preview" | null
  const [preview, setPreview] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [err, setErr] = useState(null);

  async function capture(kind) {
    if (item.status !== "accepted" && item.status !== "implemented") {
      setErr("Mark this recommendation Accepted (or Implemented) first.");
      return;
    }
    setBusy(kind);
    setErr(null);
    try {
      const body = {
        kind,
        window_days: Number(windowDays) || 14,
        note: (kind === "before" ? noteBefore : noteAfter).trim() || undefined,
      };
      await apiClient.post(`/admin/effectiveness/recommendations/${item.rec_id}/snapshot`, body);
      if (kind === "before") setNoteBefore("");
      else setNoteAfter("");
      await onChanged();
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(null);
    }
  }

  async function clear(kind) {
    if (!window.confirm(`Clear the ${kind} snapshot?`)) return;
    try {
      await apiClient.delete(`/admin/effectiveness/recommendations/${item.rec_id}/snapshot/${kind}`);
      await onChanged();
    } catch (e) { setErr(e?.response?.data?.detail || e.message); }
  }

  async function loadPreview() {
    setBusy("preview");
    setErr(null);
    try {
      const r = await apiClient.get(`/admin/effectiveness/snapshot/preview?window_days=${Number(windowDays) || 14}`);
      setPreview(r.data);
      setShowPreview(true);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(null);
    }
  }

  const before = item.before_snapshot || null;
  const after = item.after_snapshot || null;
  const canCapture = item.status === "accepted" || item.status === "implemented";

  return (
    <div className="rounded-xl border p-3.5"
      style={{ background: "rgba(79,70,229,0.04)", borderColor: "rgba(79,70,229,0.20)" }}
      data-testid={`rec-snapshot-panel-${item.rec_id}`}
    >
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <TrendingUp size={13} className="text-[var(--indigo)]" />
        <span className="text-xs uppercase tracking-wider font-semibold text-[var(--indigo)]">
          Before / After snapshot (auto from analytics)
        </span>
        {!canCapture && (
          <span className="text-[11px] text-[var(--ink-3)]">
            · Mark as Accepted to enable
          </span>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-3 items-end">
        <label className="block text-xs sm:col-span-1">
          <span className="block uppercase tracking-wider text-[var(--ink-3)] font-semibold mb-1">Window (days)</span>
          <input
            type="number" min={1} max={90}
            value={windowDays}
            onChange={(e) => setWindowDays(e.target.value)}
            className="input-base w-full"
            data-testid={`rec-snap-window-${item.rec_id}`}
          />
        </label>
        <button
          type="button"
          onClick={loadPreview}
          disabled={busy === "preview"}
          className="btn-ghost text-xs justify-center sm:col-span-2"
          data-testid={`rec-snap-preview-${item.rec_id}`}
        >
          {busy === "preview" ? "Loading…" : showPreview ? "Refresh today's available metrics" : "Preview today's available metrics"}
        </button>
      </div>

      {showPreview && preview && (
        <div className="mb-3 rounded-lg bg-white border border-[var(--line)] p-2.5">
          <div className="text-[10px] uppercase tracking-wider font-semibold text-[var(--ink-3)] mb-1.5">
            Preview · last {preview.window_days}d window
          </div>
          <SnapshotMetricsTable metrics={preview.metrics} compact />
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <SnapshotSlot
          label="Before snapshot"
          accent="var(--indigo)"
          snap={before}
          note={noteBefore}
          setNote={setNoteBefore}
          busy={busy === "before"}
          disabled={!canCapture}
          onCapture={() => capture("before")}
          onClear={() => clear("before")}
          testidPrefix={`rec-snap-before-${item.rec_id}`}
        />
        <SnapshotSlot
          label="After snapshot"
          accent="var(--emerald)"
          snap={after}
          note={noteAfter}
          setNote={setNoteAfter}
          busy={busy === "after"}
          disabled={!canCapture}
          onCapture={() => capture("after")}
          onClear={() => clear("after")}
          testidPrefix={`rec-snap-after-${item.rec_id}`}
        />
      </div>

      {err && (
        <div className="mt-2 text-xs rounded-lg px-3 py-2"
          style={{ background: "rgba(255,92,42,0.08)", color: "var(--accent)" }}
        >
          {err}
        </div>
      )}
    </div>
  );
}


function SnapshotSlot({ label, accent, snap, note, setNote, busy, disabled, onCapture, onClear, testidPrefix }) {
  const hasSnap = !!snap;
  return (
    <div className="rounded-lg p-3 bg-white border border-[var(--line)]" data-testid={`${testidPrefix}-slot`}>
      <div className="flex items-center justify-between gap-2 mb-1.5">
        <span className="text-xs uppercase tracking-wider font-semibold" style={{ color: accent }}>
          {label}
        </span>
        {hasSnap && (
          <button
            type="button"
            onClick={onClear}
            className="text-[var(--ink-3)] hover:text-[var(--accent)]"
            data-testid={`${testidPrefix}-clear`}
            aria-label="Clear snapshot"
            title="Clear"
          >
            <Trash2 size={12} />
          </button>
        )}
      </div>

      {hasSnap ? (
        <div className="space-y-1.5">
          <div className="text-[11px] text-[var(--ink-3)]">
            {new Date(snap.captured_at).toLocaleString()} · last {snap.window_days}d
            {snap.captured_by ? ` · ${snap.captured_by}` : ""}
          </div>
          {snap.note && (
            <div className="text-xs italic text-[var(--ink-2)]">&ldquo;{snap.note}&rdquo;</div>
          )}
          <SnapshotMetricsTable metrics={snap.metrics || {}} compact />
          <button
            type="button"
            onClick={onCapture}
            disabled={busy || disabled}
            className="btn-ghost text-[11px] mt-1.5"
            data-testid={`${testidPrefix}-recapture`}
          >
            {busy ? "Capturing…" : "Recapture"}
          </button>
        </div>
      ) : (
        <div className="space-y-2">
          <input
            type="text"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Optional note"
            className="input-base w-full text-xs"
            disabled={disabled}
            data-testid={`${testidPrefix}-note`}
          />
          <button
            type="button"
            onClick={onCapture}
            disabled={busy || disabled}
            className="btn-primary w-full justify-center text-xs"
            style={{ background: accent, borderColor: accent }}
            data-testid={`${testidPrefix}-capture`}
          >
            {busy ? "Capturing…" : `Capture ${label.split(" ")[0]} Snapshot`}
          </button>
        </div>
      )}
    </div>
  );
}


function SnapshotMetricsTable({ metrics, compact }) {
  if (!metrics || Object.keys(metrics).length === 0) {
    return <div className="text-xs text-[var(--ink-3)]">—</div>;
  }
  return (
    <table className="w-full text-xs">
      <tbody>
        {Object.entries(metrics).map(([k, v]) => {
          const available = v.source === "auto_mongo";
          return (
            <tr key={k} className="border-t border-[var(--line)] first:border-t-0">
              <td className={`py-0.5 pr-2 ${compact ? "" : "font-semibold"}`}>{METRIC_LABELS[k] || k}</td>
              <td className="py-0.5 text-right font-mono">
                {available ? (
                  <span style={{ color: "var(--ink)" }}>{v.value}</span>
                ) : (
                  <span className="text-[var(--ink-3)] italic"
                    title={v.reason || "Not available"}
                  >
                    Not available
                  </span>
                )}
              </td>
              <td className="py-0.5 pl-1 text-right" style={{ width: 56 }}>
                <span className="pill" style={{
                  background: available ? "rgba(14,159,110,0.08)" : "rgba(15,15,18,0.04)",
                  color: available ? "var(--emerald)" : "var(--ink-3)",
                  borderColor: available ? "rgba(14,159,110,0.30)" : "var(--line)",
                  fontSize: "0.6rem",
                  padding: "1px 6px",
                }}>
                  {available ? "AUTO" : "N/A"}
                </span>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

// ---------------------------------------------------------------------------
// Stage Panel — generate-and-stage prompt for Emergent (no outbound HTTP)
// ---------------------------------------------------------------------------

function StagePanel({ item, onChanged }) {
  const [busy, setBusy] = useState(null);
  const [err, setErr] = useState(null);
  const [copied, setCopied] = useState(false);

  const isStaged = item.status === "auto_staged" || item.status === "staged";
  const canStageManually = item.status === "accepted" || item.status === "auto_staged" || item.status === "staged";
  const autoEligible = !!item.auto_eligible;
  const prompt = item.emergent_prompt || "";

  async function stage() {
    setBusy("stage"); setErr(null);
    try {
      await apiClient.post(`/admin/effectiveness/recommendations/${item.rec_id}/stage`);
      await onChanged();
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally { setBusy(null); }
  }
  async function regenerate() {
    setBusy("regen"); setErr(null);
    try {
      await apiClient.post(`/admin/effectiveness/recommendations/${item.rec_id}/regenerate-prompt`);
      await onChanged();
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally { setBusy(null); }
  }
  async function copy() {
    try {
      await navigator.clipboard.writeText(prompt);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) { setErr("Clipboard blocked — select text and copy manually."); }
  }

  return (
    <div className="rounded-xl border p-3.5"
      style={{ background: autoEligible ? "rgba(14,159,110,0.04)" : "rgba(234,179,8,0.04)", borderColor: autoEligible ? "rgba(14,159,110,0.30)" : "rgba(234,179,8,0.30)" }}
      data-testid={`rec-stage-panel-${item.rec_id}`}
    >
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <Zap size={13} style={{ color: autoEligible ? "var(--emerald)" : "#a16207" }} />
        <span className="text-xs uppercase tracking-wider font-semibold" style={{ color: autoEligible ? "var(--emerald)" : "#a16207" }}>
          Emergent Staging · {autoEligible ? "Auto-eligible" : "Requires manual approval"}
        </span>
        {item.risk_classification && (
          <span className="text-[10px] text-[var(--ink-3)] uppercase tracking-wider">
            {item.risk_classification.replace(/_/g, " ")}
          </span>
        )}
      </div>

      {Array.isArray(item.qualification_reasons) && item.qualification_reasons.length > 0 && (
        <div className="text-[11px] text-[var(--ink-2)] mb-1.5">
          <strong className="text-[var(--emerald)]">Qualifies because:</strong> {item.qualification_reasons.join(" · ")}
        </div>
      )}
      {Array.isArray(item.risk_reasons) && item.risk_reasons.length > 0 && (
        <div className="text-[11px] text-[var(--ink-2)] mb-1.5">
          <strong style={{ color: "#a16207" }}>Held for review because:</strong> {item.risk_reasons.join(" · ")}
        </div>
      )}

      {prompt && (
        <details className="mt-2">
          <summary className="text-xs font-semibold cursor-pointer text-[var(--ink-2)]">View implementation prompt</summary>
          <pre className="mt-2 text-[11px] whitespace-pre-wrap bg-white border border-[var(--line)] rounded p-2 max-h-72 overflow-auto"
            data-testid={`rec-stage-prompt-${item.rec_id}`}
          >{prompt}</pre>
        </details>
      )}

      <div className="mt-3 flex flex-wrap gap-2">
        {!isStaged && (
          <button type="button" onClick={stage} disabled={busy !== null || (!autoEligible && !canStageManually)}
            className="btn-primary text-xs"
            style={!autoEligible && !canStageManually ? { opacity: 0.5 } : undefined}
            title={!autoEligible && !canStageManually ? "Accept this recommendation manually first." : undefined}
            data-testid={`rec-stage-${item.rec_id}`}>
            {busy === "stage" ? "Staging…" : autoEligible ? "Auto-Stage for Emergent" : "Stage for Emergent"}
          </button>
        )}
        {prompt && (
          <button type="button" onClick={copy} className="btn-ghost text-xs"
            data-testid={`rec-stage-copy-${item.rec_id}`}>
            {copied ? "Copied ✓" : "Copy Implementation Prompt"}
          </button>
        )}
        {prompt && (
          <button type="button" onClick={regenerate} disabled={busy !== null} className="btn-ghost text-xs"
            data-testid={`rec-stage-regenerate-${item.rec_id}`}>
            {busy === "regen" ? "Regenerating…" : "Regenerate prompt"}
          </button>
        )}
        {isStaged && item.staged_at && (
          <span className="text-[11px] text-[var(--ink-3)] self-center">
            Staged {new Date(item.staged_at).toLocaleString()} · {item.approval_type}
          </span>
        )}
      </div>

      {err && (
        <div className="mt-2 text-xs rounded-lg px-3 py-2"
          style={{ background: "rgba(255,92,42,0.08)", color: "var(--accent)" }}
        >{err}</div>
      )}
    </div>
  );
}


// ---------------------------------------------------------------------------
// Top Recommendations Requiring Approval — manual queue
// ---------------------------------------------------------------------------

function TopRequiringApproval({ items, onChange }) {
  const queue = React.useMemo(() => {
    return (items || [])
      .filter((it) => it.status === "needs_review" && !it.auto_eligible)
      .sort((a, b) => {
        const r = { P0: 0, P1: 1, P2: 2 };
        if ((r[a.priority] ?? 9) !== (r[b.priority] ?? 9)) return (r[a.priority] ?? 9) - (r[b.priority] ?? 9);
        return (b.num_personas_affected || 0) - (a.num_personas_affected || 0);
      })
      .slice(0, 8);
  }, [items]);

  if (!queue.length) return null;

  return (
    <div className="brut p-4" style={{ background: "rgba(234,179,8,0.04)", borderColor: "rgba(234,179,8,0.30)" }}
      data-testid="top-requiring-approval">
      <div className="flex items-center gap-2 mb-3">
        <AlertOctagon size={16} style={{ color: "#a16207" }} />
        <h3 className="font-display text-base tracking-tight">Top Recommendations Requiring Approval</h3>
        <span className="text-xs text-[var(--ink-3)]">— prioritized by P0 + persona count + impact</span>
      </div>
      <div className="space-y-2">
        {queue.map((it) => (
          <div key={it.rec_id} className="rounded-lg bg-white border border-[var(--line)] p-2.5 text-sm"
            data-testid={`top-approval-row-${it.rec_id}`}>
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <RecIdBadge seq={it.display_seq} />
              <PriorityChip priority={it.priority} />
              <span className="text-xs text-[var(--ink-3)]">{it.num_personas_affected}/5 personas</span>
              {Array.isArray(it.risk_reasons) && it.risk_reasons.length > 0 && (
                <span className="text-[10px]" style={{ color: "#a16207" }}>
                  ⚠ {it.risk_reasons[0]}{it.risk_reasons.length > 1 ? " +" + (it.risk_reasons.length - 1) + " more" : ""}
                </span>
              )}
            </div>
            <div className="leading-snug">{it.change}</div>
            {it.expected_lift && <div className="text-xs text-[var(--emerald)] mt-1">↑ {it.expected_lift}</div>}
            <div className="mt-2 flex gap-2">
              <button type="button" className="btn-ghost text-xs"
                onClick={async () => {
                  try {
                    await apiClient.patch(`/admin/effectiveness/recommendations/${it.rec_id}`, { status: "accepted", expected_impact_area: "first_lesson_starts" });
                    await onChange();
                  } catch (e) { window.alert(e?.response?.data?.detail || e.message); }
                }}
                data-testid={`top-approval-accept-${it.rec_id}`}>Accept</button>
              <button type="button" className="btn-ghost text-xs"
                onClick={async () => {
                  const reason = window.prompt("Rejection reason (required):");
                  if (!reason || !reason.trim()) return;
                  try {
                    await apiClient.patch(`/admin/effectiveness/recommendations/${it.rec_id}`, { status: "rejected", rejection_reason: reason });
                    await onChange();
                  } catch (e) { window.alert(e?.response?.data?.detail || e.message); }
                }}
                data-testid={`top-approval-reject-${it.rec_id}`}>Reject</button>
              <span className="text-[10px] self-center text-[var(--ink-3)]">or expand the row below for full review</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}



// ─── Effectiveness Agent Q&A ────────────────────────────────────────────────
// Admin-only diagnostic Q&A box. Sends free-form questions to the backend
// /api/admin/effectiveness/ask endpoint which retrieves the latest reports,
// batches, recommendations, snapshots, and conversion metrics, and asks
// Claude Sonnet 4.5 to synthesize an answer in a strict JSON shape.

const QA_QUICK_PROMPTS = [
  "Why are users not engaging in the lessons?",
  "What is the biggest blocker to first lesson starts?",
  "What should I fix first?",
  "Which recommendations are highest impact?",
  "Why do visitors leave before starting?",
  "What course of action should I take today?",
  "What changed after the last implemented fix?",
  "Which persona is least likely to start?",
  "What are the top 3 fixes to increase lesson starts?",
];

function EffectivenessAgentQA() {
  const [question, setQuestion] = useState(() => loadLS("qa_draft", ""));
  const [answer, setAnswer] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => { saveLS("qa_draft", question); }, [question]);

  async function loadHistory() {
    try {
      const r = await apiClient.get("/admin/effectiveness/qa-history?limit=10");
      setHistory(r.data.items || []);
    } catch (_e) { /* non-fatal */ }
  }
  useEffect(() => { loadHistory(); }, []);

  async function submit(q) {
    const text = (q ?? question).trim();
    if (!text) return;
    setLoading(true);
    setError(null);
    setAnswer(null);
    try {
      const r = await apiClient.post("/admin/effectiveness/ask", { question: text });
      setAnswer(r.data);
      saveLS("qa_draft", ""); // clear once successful
      setQuestion("");
      loadHistory();
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="brut p-6 md:p-7" data-testid="effectiveness-qa">
      <div className="flex items-center gap-2.5 mb-1.5">
        <MessageSquare size={18} className="text-[var(--indigo)]" />
        <h2 className="font-display text-xl tracking-tight">Ask the Effectiveness Agent</h2>
        <span className="pill text-[10px]" style={{
          background: "rgba(79,70,229,0.10)",
          color: "var(--indigo)",
          borderColor: "rgba(79,70,229,0.30)",
        }}>Admin-only · Read-only</span>
      </div>
      <p className="text-xs text-[var(--ink-3)] max-w-2xl">
        Ask why users are not starting lessons, what to fix first, or what the data is
        showing. Answers pull from the latest persona reports, batches, the Recommendation
        Tracker, before/after snapshots, and 14/30-day conversion metrics. Read-only —
        never mutates recommendations.
      </p>

      <div className="mt-4">
        <textarea
          data-testid="effectiveness-qa-input"
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          placeholder="Ask why users are not starting lessons, what to fix first, or what the data is showing…"
          className="input-base w-full"
          style={{ minHeight: 90, lineHeight: 1.4, padding: "10px 12px" }}
          maxLength={2000}
          onKeyDown={(e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === "Enter") submit();
          }}
        />
        <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
          <div className="text-[10px] text-[var(--ink-3)]">
            Tip: ⌘/Ctrl + Enter to submit · {question.length}/2000
          </div>
          <button
            type="button"
            className="btn-primary"
            onClick={() => submit()}
            disabled={loading || !question.trim()}
            data-testid="effectiveness-qa-submit"
          >
            {loading ? <><span className="spinner" /> Thinking…</> : <><Send size={14} /> Ask Agent</>}
          </button>
        </div>
      </div>

      <div className="mt-3 flex flex-wrap gap-1.5" data-testid="effectiveness-qa-quickprompts">
        {QA_QUICK_PROMPTS.map((p, i) => (
          <button
            key={i}
            type="button"
            className="pill"
            style={{
              fontSize: "0.7rem",
              cursor: "pointer",
              background: "var(--bg-2)",
              color: "var(--ink-2)",
              borderColor: "var(--line)",
            }}
            onClick={() => { setQuestion(p); submit(p); }}
            disabled={loading}
            data-testid={`effectiveness-qa-quick-${i}`}
          >
            {p}
          </button>
        ))}
      </div>

      {error && (
        <div className="mt-4 text-sm rounded-lg px-3 py-2 flex items-start gap-2"
          style={{ background: "rgba(255,92,42,0.08)", color: "var(--accent)", border: "1px solid rgba(255,92,42,0.30)" }}
          data-testid="effectiveness-qa-error"
        >
          <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {answer && <QAAnswerCard answer={answer} />}

      <div className="mt-5 border-t border-[var(--line)] pt-4">
        <button
          type="button"
          className="text-xs text-[var(--ink-3)] hover:text-[var(--ink-1)] inline-flex items-center gap-1"
          onClick={() => setHistoryOpen((v) => !v)}
          data-testid="effectiveness-qa-history-toggle"
        >
          <ListChecks size={12} />
          {historyOpen ? "Hide" : "Show"} recent questions ({history.length})
        </button>
        {historyOpen && (
          <div className="mt-3 space-y-2" data-testid="effectiveness-qa-history-list">
            {history.length === 0 ? (
              <div className="text-xs text-[var(--ink-3)]">No questions asked yet.</div>
            ) : history.map((h) => (
              <button
                key={h.qa_id}
                type="button"
                className="w-full text-left rounded-lg px-3 py-2 border border-[var(--line)] hover:bg-[var(--bg-2)] transition-colors"
                onClick={() => setAnswer({ ...h.answer, qa_id: h.qa_id, asked_at: h.asked_at, question: h.question })}
                data-testid={`effectiveness-qa-history-${h.qa_id}`}
              >
                <div className="text-xs text-[var(--ink-1)] font-medium truncate">{h.question}</div>
                <div className="text-[10px] text-[var(--ink-3)] mt-0.5">
                  {h.asked_at ? new Date(h.asked_at).toLocaleString() : ""}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function QAAnswerCard({ answer }) {
  const priority = answer?.priority || "P1";
  const priColor = PRIORITY_COLOR[priority] || PRIORITY_COLOR.P1;
  const [copied, setCopied] = useState(false);
  async function copyPrompt() {
    if (!answer?.implementation_prompt) return;
    try {
      await navigator.clipboard.writeText(answer.implementation_prompt);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch (_e) { /* clipboard blocked */ }
  }
  return (
    <div
      className="mt-4 rounded-xl p-4 md:p-5 border border-[var(--line)] bg-[var(--bg-2)] space-y-3"
      data-testid="effectiveness-qa-answer"
    >
      {answer.question && (
        <div className="text-[11px] uppercase tracking-wider text-[var(--ink-3)] font-semibold">
          You asked
          <span className="ml-2 text-[var(--ink-1)] text-sm normal-case font-normal tracking-normal">
            “{answer.question}”
          </span>
        </div>
      )}

      <div data-testid="qa-direct-answer">
        <div className="text-[10px] uppercase tracking-wider text-[var(--ink-3)] font-semibold mb-1">
          Direct answer
        </div>
        <p className="text-sm text-[var(--ink-1)] leading-relaxed">{answer.direct_answer || "—"}</p>
      </div>

      {Array.isArray(answer.evidence) && answer.evidence.length > 0 && (
        <div data-testid="qa-evidence">
          <div className="text-[10px] uppercase tracking-wider text-[var(--ink-3)] font-semibold mb-1">
            Evidence
          </div>
          <ul className="text-sm text-[var(--ink-2)] space-y-1 list-disc pl-5">
            {answer.evidence.map((e, i) => <li key={i}>{e}</li>)}
          </ul>
        </div>
      )}

      {answer.likely_cause && (
        <div data-testid="qa-likely-cause">
          <div className="text-[10px] uppercase tracking-wider text-[var(--ink-3)] font-semibold mb-1">
            Likely cause
          </div>
          <p className="text-sm text-[var(--ink-1)] leading-relaxed">{answer.likely_cause}</p>
        </div>
      )}

      {Array.isArray(answer.recommended_actions) && answer.recommended_actions.length > 0 && (
        <div data-testid="qa-recommended-actions">
          <div className="text-[10px] uppercase tracking-wider text-[var(--ink-3)] font-semibold mb-1">
            Recommended course of action
          </div>
          <ul className="text-sm text-[var(--ink-1)] space-y-1.5">
            {answer.recommended_actions.map((a, i) => (
              <li key={i} className="flex items-start gap-2">
                <Lightbulb size={14} className="mt-0.5 flex-shrink-0 text-[var(--indigo)]" />
                <span>{a}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="flex items-center gap-2 flex-wrap" data-testid="qa-priority">
        <span className="text-[10px] uppercase tracking-wider text-[var(--ink-3)] font-semibold">
          Priority
        </span>
        <span
          className="pill"
          style={{
            background: priColor.bg,
            color: priColor.color,
            borderColor: priColor.border,
            fontSize: "0.72rem",
            fontWeight: 600,
          }}
        >
          {priority}
        </span>
      </div>

      {answer.implementation_prompt && answer.implementation_prompt.trim() && (
        <div className="rounded-lg border border-[var(--line)] bg-white p-3" data-testid="qa-impl-prompt">
          <div className="flex items-center justify-between mb-1.5">
            <div className="text-[10px] uppercase tracking-wider text-[var(--ink-3)] font-semibold">
              Suggested implementation prompt
            </div>
            <button
              type="button"
              onClick={copyPrompt}
              className="btn-ghost text-xs"
              data-testid="qa-impl-copy"
            >
              {copied ? <><Check size={12} /> Copied</> : <><Copy size={12} /> Copy</>}
            </button>
          </div>
          <pre className="text-xs text-[var(--ink-2)] whitespace-pre-wrap font-mono leading-relaxed" style={{ maxHeight: 320, overflow: "auto" }}>
            {answer.implementation_prompt}
          </pre>
        </div>
      )}

      {Array.isArray(answer.missing_data) && answer.missing_data.length > 0 && (
        <div
          className="rounded-lg p-3 border"
          style={{
            background: "rgba(234,179,8,0.08)",
            borderColor: "rgba(234,179,8,0.30)",
          }}
          data-testid="qa-missing-data"
        >
          <div className="text-[10px] uppercase tracking-wider text-[#a16207] font-semibold mb-1">
            Missing data
          </div>
          <ul className="text-xs text-[#a16207] space-y-1 list-disc pl-5">
            {answer.missing_data.map((m, i) => <li key={i}>{m}</li>)}
          </ul>
        </div>
      )}
    </div>
  );
}


// ─── Visitor Attention Report (Phase 1 MVP) ─────────────────────────────────
// Reads from POST /api/admin/visitors/attention-report + /funnel and renders:
//   • 14 summary metrics (avg time, bounce %, action breakdowns, anti-action
//     buckets like "scrolled 25%+ but never clicked")
//   • Most-viewed pages, most-clicked CTAs, highest-exit pages
//   • The 9-stage conversion funnel with drop-off % between every stage

const FUNNEL_STAGE_LABELS = {
  visit:                "Visit",
  cta_click:            "Any CTA click",
  assessment_start:     "Assessment start",
  assessment_complete:  "Assessment complete",
  first_challenge_start:"First challenge start",
  lesson_start:         "Lesson start",
  lesson_complete:      "Lesson complete",
  signup_click:         "Signup click",
  checkout_click:       "Checkout click",
};

function VisitorAttentionReport() {
  const [days, setDays] = useState(() => parseInt(loadLS("vis_window_days", "14"), 10) || 14);
  const [report, setReport] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => { saveLS("vis_window_days", String(days)); }, [days]);

  async function load() {
    setLoading(true); setError(null);
    try {
      const [r1, r2] = await Promise.all([
        apiClient.get(`/admin/visitors/attention-report?days=${days}`),
        apiClient.get(`/admin/visitors/funnel?days=${days}`),
      ]);
      setReport(r1.data);
      setFunnel(r2.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [days]);

  const summary = report?.summary || {};
  const empty = report && report.total_sessions === 0;

  return (
    <div className="brut p-6 md:p-7" data-testid="visitor-attention-report">
      <div className="flex items-center gap-2.5 mb-1.5 flex-wrap">
        <Eye size={18} className="text-[var(--indigo)]" />
        <h2 className="font-display text-xl tracking-tight">Visitor Attention Report</h2>
        <span className="pill text-[10px]" style={{
          background: "rgba(79,70,229,0.10)",
          color: "var(--indigo)",
          borderColor: "rgba(79,70,229,0.30)",
        }}>Real visitors only · Bots/Admins/AI sims excluded</span>
      </div>
      <p className="text-xs text-[var(--ink-3)] max-w-2xl">
        Where real visitors actually spend their time, what they click, and where they bail out before
        starting a lesson. Excludes admin sessions, AI persona simulations, internal QA traffic, and
        known bots/crawlers.
      </p>

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <label className="text-xs text-[var(--ink-3)] inline-flex items-center gap-2">
          <span className="uppercase tracking-wider font-semibold">Window</span>
          <select
            value={days}
            onChange={(e) => setDays(parseInt(e.target.value, 10) || 14)}
            className="input-base text-xs"
            style={{ padding: "4px 8px" }}
            data-testid="visitor-window-select"
          >
            <option value={1}>Last 24 hours</option>
            <option value={7}>Last 7 days</option>
            <option value={14}>Last 14 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
        </label>
        <button type="button" className="btn-ghost" onClick={load} data-testid="visitor-refresh-btn">
          <RefreshCcw size={12} /> Refresh
        </button>
        {report && (
          <div className="text-[11px] text-[var(--ink-3)] ml-auto" data-testid="visitor-counts-summary">
            {report.total_sessions} real session(s) ·{" "}
            <span title="Bots, admin sessions, AI persona simulations, internal QA">
              {report.excluded_sessions} excluded
            </span>
          </div>
        )}
      </div>

      {error && (
        <div className="mt-4 text-sm rounded-lg px-3 py-2"
          style={{ background: "rgba(255,92,42,0.08)", color: "var(--accent)" }}
          data-testid="visitor-error"
        >
          {error}
        </div>
      )}

      {loading ? (
        <div className="mt-6 text-center text-sm text-[var(--ink-3)]">
          <span className="spinner mr-2" /> Loading…
        </div>
      ) : empty ? (
        <div className="mt-5 rounded-lg p-4 border border-[var(--line)] bg-[var(--bg-2)] text-sm text-[var(--ink-3)]"
          data-testid="visitor-empty-state"
        >
          {report?.empty_state_hint || "No real-visitor sessions recorded in this window yet. Wait for traffic, or open the site in an incognito tab to seed the tracker."}
        </div>
      ) : (
        <>
          <VisitorSummaryGrid summary={summary} totalSessions={report?.total_sessions || 0} />
          <VisitorLists report={report} />
          {funnel && <VisitorFunnel funnel={funnel} />}
        </>
      )}
    </div>
  );
}

function VisitorSummaryGrid({ summary, totalSessions }) {
  // Compose 14 cards mirroring the spec. Each card shows the value + a
  // short label that explains what the metric means.
  const fmtPct = (n) => `${(typeof n === "number" ? n : 0).toFixed(1)}%`;
  const cards = [
    { label: "Avg time on site", value: `${summary.avg_time_on_site_sec ?? 0}s`, testid: "vis-metric-avg-time" },
    { label: "Avg pages / session", value: summary.avg_pages_per_session ?? 0, testid: "vis-metric-avg-pages" },
    { label: "Avg clicks / session", value: summary.avg_clicks_per_session ?? 0, testid: "vis-metric-avg-clicks" },
    { label: "Avg scroll depth", value: `${summary.avg_scroll_max_pct ?? 0}%`, testid: "vis-metric-avg-scroll" },
    { label: "Bounced (no clicks)", value: `${summary.bounced ?? 0} (${fmtPct(summary.bounced_pct)})`, testid: "vis-metric-bounced", tone: "amber" },
    { label: "Bounced under 10s", value: summary.under_10s_bounces ?? 0, testid: "vis-metric-under-10s", tone: "amber" },
    { label: "30s+ & still no action", value: summary.over_30s_no_action ?? 0, testid: "vis-metric-30s-no-action", tone: "amber" },
    { label: "Scrolled but didn't click", value: summary.scrolled_no_click ?? 0, testid: "vis-metric-scroll-noclick", tone: "amber" },
    { label: "Any CTA clicked", value: summary.any_cta_clicked ?? 0, testid: "vis-metric-cta-click", tone: "emerald" },
    { label: "Reached Skill Check", value: summary.reached_assessment ?? 0, testid: "vis-metric-reached-assessment", tone: "emerald" },
    { label: "Skill Check started but not completed", value: summary.skillcheck_started_not_completed ?? 0, testid: "vis-metric-skillcheck-abandoned", tone: "amber" },
    { label: "Started a lesson", value: summary.started_lesson ?? 0, testid: "vis-metric-started-lesson", tone: "emerald" },
    { label: "Reached pricing before any lesson", value: summary.pricing_before_lesson ?? 0, testid: "vis-metric-pricing-pre-lesson", tone: "amber" },
    { label: "Total real sessions in window", value: totalSessions, testid: "vis-metric-total", tone: "indigo" },
  ];
  const toneStyle = (t) => ({
    emerald: { color: "var(--emerald)", border: "rgba(14,159,110,0.20)", bg: "rgba(14,159,110,0.04)" },
    amber:   { color: "#a16207",       border: "rgba(234,179,8,0.25)",   bg: "rgba(234,179,8,0.05)" },
    indigo:  { color: "var(--indigo)", border: "rgba(79,70,229,0.25)",   bg: "rgba(79,70,229,0.04)" },
  }[t] || { color: "var(--ink-1)", border: "var(--line)", bg: "var(--bg-2)" });
  return (
    <div className="mt-5 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2.5" data-testid="visitor-summary-grid">
      {cards.map((c, i) => {
        const t = toneStyle(c.tone);
        return (
          <div key={i} className="rounded-lg p-3 border"
            style={{ borderColor: t.border, background: t.bg }}
            data-testid={c.testid}
          >
            <div className="text-[10px] uppercase tracking-wider text-[var(--ink-3)] font-semibold">{c.label}</div>
            <div className="text-xl font-semibold mt-0.5" style={{ color: t.color }}>{c.value}</div>
          </div>
        );
      })}
    </div>
  );
}

function VisitorLists({ report }) {
  const cell = (rows, key, valKey, testid, label) => (
    <div className="rounded-xl p-4 border border-[var(--line)] bg-white" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider text-[var(--ink-3)] font-semibold mb-2">{label}</div>
      {(rows || []).length === 0 ? (
        <div className="text-xs text-[var(--ink-3)] italic">No data yet.</div>
      ) : (
        <ul className="space-y-1.5">
          {(rows || []).slice(0, 8).map((r, i) => (
            <li key={i} className="flex items-center justify-between text-sm">
              <span className="truncate pr-2 text-[var(--ink-1)]">{r[key] || "—"}</span>
              <span className="text-[var(--ink-2)] font-mono text-xs">{r[valKey]}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
  return (
    <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="visitor-lists">
      {cell(report?.most_viewed_pages, "page", "views", "visitor-list-pages", "Most-viewed pages")}
      {cell(report?.most_clicked_ctas, "cta", "clicks", "visitor-list-ctas", "Most-clicked CTAs")}
      {cell(report?.highest_exit_pages, "page", "exits", "visitor-list-exits", "Highest-exit pages")}
    </div>
  );
}

function VisitorFunnel({ funnel }) {
  const stages = funnel?.stages || [];
  const maxCount = Math.max(1, ...stages.map((s) => s.count));
  return (
    <div className="mt-5 rounded-xl p-4 md:p-5 border border-[var(--line)] bg-white" data-testid="visitor-funnel">
      <div className="text-[10px] uppercase tracking-wider text-[var(--ink-3)] font-semibold mb-3">
        Conversion funnel · {funnel.total_sessions} sessions · {funnel.window_days}d
      </div>
      <div className="space-y-2">
        {stages.map((s, i) => {
          const pct = (s.count / maxCount) * 100;
          return (
            <div key={s.stage} data-testid={`visitor-funnel-stage-${s.stage}`} className="grid grid-cols-12 gap-2 items-center text-xs">
              <div className="col-span-3 md:col-span-3 text-[var(--ink-1)] font-medium truncate">
                {FUNNEL_STAGE_LABELS[s.stage] || s.stage}
              </div>
              <div className="col-span-6 md:col-span-6">
                <div className="h-3 rounded-full bg-[var(--bg-2)] overflow-hidden">
                  <div
                    className="h-full"
                    style={{
                      width: `${pct}%`,
                      background: "linear-gradient(90deg, var(--indigo) 0%, var(--accent) 100%)",
                    }}
                  />
                </div>
              </div>
              <div className="col-span-2 text-right font-mono text-[var(--ink-2)]">{s.count}</div>
              <div className="col-span-1 text-right text-[var(--ink-3)]" title={`Drop-off from previous stage: ${s.step_drop_off_pct}%`}>
                {i === 0 ? "—" : `-${s.step_drop_off_pct}%`}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

